import React, { useState, useRef, useEffect } from "react";
import { useOtherChargeOptions, applyOtherChargeToEntry } from "../utils/otherChargeOptions";
import { InlineAutoCompleteInput } from "../components/OtherAmountModal";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import "../assets/css/theme-table-header.css";
import {
  REPAIRING_ORDER_LINE_ERROR_OPTIONS,
  collectLineItemFieldErrors,
  lineCellErrorStyle,
  pruneLineFieldErrors,
} from "../utils/lineItemFieldErrors";
import { getUserData, node_imageBaseUrl } from "./const";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  sanitizeMobileInput,
  getMobileValidationError,
  isValidMobileNumber,
} from "../utils/mobileInput";
import { getEmailValidationError } from "../utils/emailInput";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import { autoRoundOff } from "../utils/autoRoundOff";
import { mapApiChargesToOtherAmountEntries } from "../utils/otherAmountCalculations";
import PaymentSection, {
  PAYMENT_METHOD_LABELS,
} from "../components/PaymentSection";

import { getActiveItems } from "../utils/statusUtils";
import {
  createCustomerUser,
  createRepairingOrderMultipart,
  createSale,
  getErpAuthState,
  createSupplier,
  isErpApiSuccess,
  listBankAccounts,
  listBillTypes,
  listCustomerUsers,
  listSuppliers,
  listOrderQueue,
  showCustomerUser,
  showOrderQueue,
  showProducts,
  showRepairingOrder,
  updateRepairingOrderMultipart,
} from "../erpClient";
import {
  fetchProductGroupOptionById,
  mergeProductGroupOptions,
  searchProductGroupSelectOptions,
  productGroupOptionDisplayName,
} from "../utils/productGroupOptions";
import { numPool } from "../utils/productGroupPool";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import {
  createSalesIdempotencyKey,
  isSalesApiSuccess,
  mapItemRateOn,
  mapLbrTypeForApi,
} from "../utils/salesErpAdapter";
import { mapCustomerUsersToPartyList } from "../utils/customerUsersErpAdapter";
import { createRepairingOrderIdempotencyKey } from "../utils/repairingOrdersErpAdapter";
import {
  buildStandardPaymentLines,
  mapApiPaymentsToUiRows,
} from "../utils/paymentLinesErpAdapter";
import { useLocation, useNavigate } from "react-router-dom";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { resolveQueueDetailsId, findOrderQueueInResponse } from "../utils/queueNavigation";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import CustomerSelection from "../components/CustomerSelection";
import Select from "react-select";
import {
  billTypeHeaderSelectStyles,
  billTypeHeaderSelectWrapStyle,
} from "../utils/billTypeHeaderSelectStyles";

const REPAIRING_ORDER_FETCH_DEDUPE_MS = 2000;

let repairingOrderShowPromise = null;
let repairingOrderShowCache = null;
let repairingOrderShowCacheId = null;
let repairingOrderShowCacheAt = 0;

let repairingBankAccountsFetchPromise = null;
let repairingBankAccountsFetchCache = null;
let repairingBankAccountsFetchCacheAt = 0;

let repairingBillTypesFetchPromise = null;
let repairingBillTypesFetchCache = null;
let repairingBillTypesFetchCacheAt = 0;

function isRepairingOrderShowSuccess(res) {
  return isErpApiSuccess(res) || res?.data != null;
}

async function requestRepairingOrderShowForEdit(
  repairingOrderId,
  { force = false, isQuote } = {},
) {
  const id = String(repairingOrderId);
  const scopeKey =
    isQuote === true ? "q" : isQuote === false ? "a" : "p";
  const cacheKey = `${id}:${scopeKey}`;
  const now = Date.now();

  if (!force) {
    if (repairingOrderShowPromise) return repairingOrderShowPromise;
    if (
      repairingOrderShowCache &&
      repairingOrderShowCacheId === cacheKey &&
      now - repairingOrderShowCacheAt < REPAIRING_ORDER_FETCH_DEDUPE_MS
    ) {
      return repairingOrderShowCache;
    }
  } else {
    repairingOrderShowPromise = null;
    repairingOrderShowCache = null;
    repairingOrderShowCacheId = null;
    repairingOrderShowCacheAt = 0;
  }

  repairingOrderShowPromise = showRepairingOrder({
    id: repairingOrderId,
    isQuote,
  })
    .then((res) => {
      if (isRepairingOrderShowSuccess(res)) {
        repairingOrderShowCache = res;
        repairingOrderShowCacheId = cacheKey;
        repairingOrderShowCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      repairingOrderShowPromise = null;
    });

  return repairingOrderShowPromise;
}

async function requestBankAccountsForRepairing({ force = false } = {}) {
  const now = Date.now();

  if (!force) {
    if (repairingBankAccountsFetchPromise) return repairingBankAccountsFetchPromise;
    if (
      repairingBankAccountsFetchCache &&
      now - repairingBankAccountsFetchCacheAt < REPAIRING_ORDER_FETCH_DEDUPE_MS
    ) {
      return repairingBankAccountsFetchCache;
    }
  } else {
    repairingBankAccountsFetchPromise = null;
    repairingBankAccountsFetchCache = null;
    repairingBankAccountsFetchCacheAt = 0;
  }

  repairingBankAccountsFetchPromise = listBankAccounts()
    .then((res) => {
      if (res?.success) {
        repairingBankAccountsFetchCache = res;
        repairingBankAccountsFetchCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      repairingBankAccountsFetchPromise = null;
    });

  return repairingBankAccountsFetchPromise;
}

async function requestBillTypesForRepairing({ force = false } = {}) {
  const now = Date.now();

  if (!force) {
    if (repairingBillTypesFetchPromise) return repairingBillTypesFetchPromise;
    if (
      repairingBillTypesFetchCache &&
      now - repairingBillTypesFetchCacheAt < REPAIRING_ORDER_FETCH_DEDUPE_MS
    ) {
      return repairingBillTypesFetchCache;
    }
  } else {
    repairingBillTypesFetchPromise = null;
    repairingBillTypesFetchCache = null;
    repairingBillTypesFetchCacheAt = 0;
  }

  repairingBillTypesFetchPromise = listBillTypes()
    .then((res) => {
      if (isErpApiSuccess(res)) {
        repairingBillTypesFetchCache = res;
        repairingBillTypesFetchCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      repairingBillTypesFetchPromise = null;
    });

  return repairingBillTypesFetchPromise;
}

const isZeroOrNullValue = (value) =>
  value === 0 ||
  value === "0" ||
  value === null ||
  value === undefined ||
  value === "";

// Build safe absolute URLs for repairing-order images coming from Node API.
// Backend may return:
// - full URL (http...)
// - "upload/..." or "uploads/..."
// - "/upload/..."
// - plain filename or subpath
const getNodeImageCandidates = (rawPath) => {
  if (!rawPath) return [];
  const p = String(rawPath).trim();
  if (!p) return [];

  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return [p];
  }

  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  const normalized = p.replace(/^\/+/, "");

  if (normalized.startsWith("upload/") || normalized.startsWith("uploads/")) {
    return [encodeURI(`${base}/${normalized}`)];
  }

  if (normalized.includes("/")) {
    return [encodeURI(`${base}/${normalized}`), encodeURI(`${base}/upload/${normalized}`)];
  }

  // Plain filename: try common locations
  return [
    encodeURI(`${base}/uploads/repairing/${normalized}`),
    encodeURI(`${base}/upload/repairing/${normalized}`),
    encodeURI(`${base}/upload/${normalized}`),
    encodeURI(`${base}/${normalized}`),
  ];
};

const getNodeImageUrl = (rawPath) => getNodeImageCandidates(rawPath)[0] || null;

/** Map order-queue `price_type` → repairing table Lbr Type */
function mapQueuePriceTypeToLbrType(priceType) {
  const t = String(priceType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  // Fixed amount
  if (t === "fixed" || t === "flat") return "Fixed";
  // Percentage (%) or Per gm / per grams → Exp Wt
  if (
    t === "percentage" ||
    t === "percent" ||
    t === "%" ||
    t === "per_rate" ||
    t === "per_gm" ||
    t === "per_gram" ||
    t === "per_grams" ||
    t.includes("percent") ||
    t.includes("per_gm") ||
    t.includes("per_gram")
  ) {
    return "Exp Wt";
  }
  return "";
}

function isQueuePercentagePriceType(priceType) {
  const t = String(priceType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return t === "percentage" || t === "percent" || t === "%" || t.includes("percent");
}

function isQueuePerGramPriceType(priceType) {
  const t = String(priceType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return (
    t === "per_rate" ||
    t === "per_gm" ||
    t === "per_gram" ||
    t === "per_grams" ||
    t.includes("per_gm") ||
    t.includes("per_gram")
  );
}

// Add this new component right before the Table component
const AddRepairingOrder = ({
  masterGoldRate,
  masterSilverRate,
  isRepairingSale = false,
  pageTitle,
  repairingOrderId: repairingOrderIdProp,
  onClose,
  viewOnly = false,
  isQuote: isQuoteProp,
}) => {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const location = useLocation();
  const navigate = useNavigate();
  const isEmbedded = typeof onClose === "function";
  const isViewOnly = Boolean(viewOnly) || isEmbedded;
  const leaveEdit = () => {
    if (isEmbedded) {
      onClose();
      return;
    }
    navigate("/repairingorder");
  };
  const queueDetailsId = resolveQueueDetailsId(location.state);
  const customOrderId = location.state?.customOrderId;
  const customerUserId = location.state?.customerUserId;
  const repairingOrderId =
    repairingOrderIdProp ||
    location.state?.repairingOrderId ||
    location.state?.id;

  const [canAddRepairingOrder, setCanAddRepairingOrder] = useState(() =>
    hasPermission(PERMISSIONS.REPAIRING_ORDER_ADD),
  );
  const [canEditRepairingOrder, setCanEditRepairingOrder] = useState(() =>
    hasPermission(PERMISSIONS.REPAIRING_ORDER_EDIT),
  );

  useEffect(() => {
    const syncPerms = () => {
      setCanAddRepairingOrder(hasPermission(PERMISSIONS.REPAIRING_ORDER_ADD));
      setCanEditRepairingOrder(hasPermission(PERMISSIONS.REPAIRING_ORDER_EDIT));
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);
  const [customOrderData, setCustomOrderData] = useState(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);
  const queueHydrationKeyRef = useRef(null);
  const [userss, setUserss] = useState([]);
  const customerSearchCacheRef = useRef(new Set());
  const karigarSearchCacheRef = useRef(new Set());
  const repairingOrderSubmitIdempotencyKeyRef = useRef(null);
  const repairingSaleIdempotencyKeyRef = useRef(null);

  const mapShowCustomerToUi = (user) => {
    if (!user?.id) return null;
    return {
      id: user.id,
      fullName: user.name || "",
      name: user.name || "",
      mobile: user.mobile || "",
      email: user.email || "",
      city: user.city || "",
      address: user.address || "",
      state: user.state || "",
      area: user.area || user.Area || user.area_name || user.areaName || "",
      aadharNo: user.aadhar_no || "",
      panNo: user.pan_no || "",
      birthDate: user.birth_date || "",
      maritalStatus: user.marital_status || "",
      anniversaryDate: user.anniversary_date || "",
      balance: user.users_balance ?? user.balance ?? "",
      saleBalance: user.sale_balance || "",
      companyName: user.company_name || "",
      companyAddress: user.company_address || "",
      gstNumber: user.GST_number || user.gst_number || "",
      reference: user.reference || "",
      status: user.status || "",
    };
  };

  const applyCustomerToForm = (customer) => {
    if (!customer) return;
    // Set id first so customer search effect does not fire listCustomerUsers
    if (customer.id != null && customer.id !== "") {
      setSelectedCustomerId(customer.id);
    }
    setCustomerMobile(customer.mobile || "");
    setCustomerMobileSearchText(customer.mobile || "");
    setCustomerName(customer.fullName || customer.name || "");
    setCustomerNameSearchText(customer.fullName || customer.name || "");
    setCustomerAddress(customer.address || "");
    setCustomerCity(customer.city || "");
    if (customer.state) {
      setSelectedState(customer.state);
    }
    setCustomerDetails((prevDetails) => ({
      ...prevDetails,
      email: customer.email || "",
      birthDate: customer.birthDate || "",
      anniversaryDate: customer.anniversaryDate || "",
      companyName: customer.companyName || "",
      companyAddress: customer.companyAddress || "",
      gstNumber: customer.gstNumber || "",
      aadharNo: customer.aadharNo || "",
      panNo: customer.panNo || "",
      maritalStatus: customer.maritalStatus || "",
      balance: customer.balance || "",
      saleBalance: customer.saleBalance || "",
      reference: customer.reference || "",
      state: customer.state || "",
      area: customer.area || "",
    }));
  };

  const mergeUserss = (party) => {
    if (!party?.length) return;
    setUserss((prev) => {
      const byId = new Map(
        (Array.isArray(prev) ? prev : []).map((u) => [String(u.id), u]),
      );
      party.forEach((u) => byId.set(String(u.id), u));
      return Array.from(byId.values()).sort(
        (a, b) => parseInt(b.id, 10) - parseInt(a.id, 10),
      );
    });
  };

  const extractCustomerFromShowResponse = (response) => {
    const d = response?.data;
    if (!d) return null;
    return (
      d.customer ??
      d.user ??
      d.item ??
      (Array.isArray(d.customers) ? d.customers[0] : null) ??
      (Array.isArray(d.items) ? d.items[0] : null) ??
      (Array.isArray(d.data) ? d.data[0] : null) ??
      (Array.isArray(d) ? d[0] : null) ??
      (d.id != null ? d : null)
    );
  };

  // Apply a queue order snapshot (from navigation or API) into the repairing form
  const applyQueueOrderToRepair = async (foundOrder) => {
    if (!foundOrder) return;
    setCustomOrderData(foundOrder);

    const paymentSource =
      Array.isArray(foundOrder.payment_details) &&
        foundOrder.payment_details.length > 0
        ? foundOrder.payment_details
        : foundOrder.payments;
    const mappedPayments = mapApiPaymentsToUiRows(paymentSource, {
      includeBankRegister: true,
    });
    if (mappedPayments) {
      setPaymentRows(mappedPayments);
    }

    const custId =
      foundOrder.customer_user_id ??
      foundOrder.user_id ??
      foundOrder.customer_id ??
      customerUserId;

    // Seed immediately from queue row so fields are not blank if showCustomerUser is slow/fails
    const snapshotName =
      foundOrder.name ||
      foundOrder.user_name ||
      foundOrder.customer_name ||
      "";
    const snapshotMobile =
      foundOrder.mobile ||
      foundOrder.user_mobile ||
      foundOrder.customer_mobile ||
      "";
    if (snapshotName || snapshotMobile || custId) {
      applyCustomerToForm({
        id: custId || null,
        fullName: snapshotName,
        name: snapshotName,
        mobile: snapshotMobile,
        address:
          foundOrder.address ||
          foundOrder.user_address ||
          foundOrder.customer_address ||
          "",
        city:
          foundOrder.city ||
          foundOrder.user_city ||
          foundOrder.customer_city ||
          "",
        area: foundOrder.area || location.state?.area || "",
        state: foundOrder.state || foundOrder.user_state || "",
      });
    }

    if (custId) {
      try {
        const response = await showCustomerUser({ id: Number(custId) });
        const raw = extractCustomerFromShowResponse(response);
        const mapped = mapShowCustomerToUi(raw);
        if (isErpApiSuccess(response) && mapped) {
          mergeUserss([mapped]);
          applyCustomerToForm(mapped);
        }
      } catch (custErr) {
        console.error("Error loading queue customer:", custErr);
      }
    }

    // Map product group IDs to names and populate form
    if (foundOrder.products && foundOrder.products.length > 0) {
      const uniquePgIds = [
        ...new Set(
          foundOrder.products
            .map(
              (product) =>
                product.product_group_id ?? product.product_group,
            )
            .filter((id) => id != null && id !== ""),
        ),
      ];
      const resolvedPgs = await Promise.all(
        uniquePgIds.map(async (id) => {
          const productWithName = foundOrder.products.find(
            (p) =>
              String(p.product_group_id ?? p.product_group) === String(id) &&
              p.product_group_name,
          );
          if (productWithName?.product_group_name) {
            return {
              value: id,
              label: productWithName.product_group_name,
              name: productWithName.product_group_name,
            };
          }
          return resolveProductGroupOption(id);
        }),
      );
      const pgById = {};
      resolvedPgs.filter(Boolean).forEach((pg) => {
        const label = pg.label || pg.name || `Group ${pg.value}`;
        pgById[pg.value] = label;
        pgById[String(pg.value)] = label;
      });

      const mappedProducts = foundOrder.products.map((product) => {
        const pgId = product.product_group_id ?? product.product_group;
        const productGroupName =
          pgById[pgId] ||
          pgById[String(pgId)] ||
          product.product_group_name ||
          pgId ||
          "N/A";
        return {
          ...product,
          product_group: pgId,
          product_group_name: productGroupName,
        };
      });

      const firstDelivery = mappedProducts.find((p) => p.delivery_date)?.delivery_date;
      if (firstDelivery) {
        setExpDate(String(firstDelivery).slice(0, 10));
      }

      // Populate the form table with order data
      const tableData = mappedProducts.map((product, index) => ({
        id: index + 1,
        data: COLUMNS.map((col) => {
          let value = "";
          let additionalProps = {};
          const lbrType = mapQueuePriceTypeToLbrType(product.price_type);
          const lbrAmount =
            product.expected_price ??
            product.expectedPrice ??
            (lbrType === "Fixed" ? product.price_value : "") ??
            "";
          const othAmt = product.oth_amt ?? product.other_amount ?? "";
          switch (col.id) {
            case "productGroup":
              value = product.product_group_name || "";
              additionalProps.productGroupId =
                product.product_group_id ?? product.product_group ?? null;
              break;
            case "pcs":
              value = product.pcs || 1;
              break;
            case "currentWeight":
              value = product.current_weight || "";
              break;
            case "expWeight":
              value = product.expected_weight || "";
              break;
            case "issueDescription":
              value = product.description || "";
              break;
            case "lType":
              // percentage / per grams → Exp Wt; fixed → Fixed
              value = lbrType;
              break;
            case "lbrPercentage":
              value = isQueuePercentagePriceType(product.price_type)
                ? product.price_value ?? ""
                : "";
              break;
            case "lbrRate":
              value = isQueuePerGramPriceType(product.price_type)
                ? product.price_value ?? ""
                : "";
              break;
            case "lbrAmount":
              value = lbrAmount !== null && lbrAmount !== undefined ? String(lbrAmount) : "";
              break;
            case "image": {
              const rawImagePath =
                product.design_photos ||
                product.design_photo ||
                product.image ||
                "";
              value = rawImagePath || "";
              return {
                id: col.id,
                value,
                imageFile: null,
                imagePreview: rawImagePath
                  ? getNodeImageUrl(rawImagePath)
                  : null,
              };
            }
            case "otherAmount":
              value = othAmt !== null && othAmt !== undefined ? String(othAmt) : "";
              break;
            case "totalAmount": {
              const lbr = parseFloat(lbrAmount) || 0;
              const oth = parseFloat(othAmt) || 0;
              value = (lbr + oth).toFixed(2);
              break;
            }
            default:
              value = "";
          }
          return { id: col.id, value, ...additionalProps };
        }),
      }));

      setTableData(tableData);
    }
  };

  // Fetch order data using modular show_order_queue
  const fetchOrderData = async (orderId) => {
    if (!orderId) return;

    setIsLoadingOrderData(true);
    try {
      let foundOrder = findOrderQueueInResponse(
        await showOrderQueue({ id: orderId }),
        orderId,
      );

      if (!foundOrder) {
        foundOrder = findOrderQueueInResponse(
          await listOrderQueue({
            search: String(orderId),
            page: 1,
            limit: 50,
          }),
          orderId,
        );
      }

      if (foundOrder) {
        await applyQueueOrderToRepair(foundOrder);
      } else {
        toast.error("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      toast.error("Failed to fetch order data");
    } finally {
      setIsLoadingOrderData(false);
    }
  };

  // Initialize repairing order form

  // Fetch customers only when searching (not on pageload)
  const fetchUserss = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";
    if (!searchText || searchText.length < 3) return [];

    try {
      const res = await listCustomerUsers({ search: searchText });
      const party = mapCustomerUsersToPartyList(res);
      if (party?.length) {
        mergeUserss(party);
        customerSearchCacheRef.current.add(searchText.toLowerCase());
        return party;
      }
      return [];
    } catch (error) {
      console.error("Error fetching customer users:", error);
      return [];
    }
  };

  // Listen for API URL changes — clear caches; do not preload lists
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in AddRepairingOrder, clearing cached lists...', event.detail);
      setUserss([]);
      customerSearchCacheRef.current = new Set();
      setKarigars([]);
      karigarSearchCacheRef.current = new Set();
      productGroupOptionsCacheRef.current = [];
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setLastSearchedProductGroupPrefix("");
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      setProductGroupPoolHeader(null);
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  // Load queue order when arriving from Order Queue (REPAIR → Repairing entry)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const seeded = location.state?.customOrderData;
    const hydrationKey = queueDetailsId
      ? `queue:${queueDetailsId}`
      : seeded?.id != null
        ? `seeded:${seeded.id}`
        : null;
    if (!hydrationKey) return;
    if (queueHydrationKeyRef.current === hydrationKey) return;
    queueHydrationKeyRef.current = hydrationKey;

    if (seeded) {
      setIsLoadingOrderData(true);
      applyQueueOrderToRepair(seeded).finally(() =>
        setIsLoadingOrderData(false),
      );
      return;
    }
    fetchOrderData(queueDetailsId);
  }, [queueDetailsId, location.state?.customOrderData]);

  // Prefill customer from navigation via show (not full customer list)
  useEffect(() => {
    if (!customerUserId || queueDetailsId) return;
    let cancelled = false;

    (async () => {
      try {
        const response = await showCustomerUser({
          id: Number(customerUserId),
        });
        if (cancelled) return;
        const raw = extractCustomerFromShowResponse(response);
        const mapped = mapShowCustomerToUi(raw);
        if (!isErpApiSuccess(response) || !mapped) return;
        mergeUserss([mapped]);
        applyCustomerToForm(mapped);
      } catch (error) {
        console.error("Error loading customerUserId:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerUserId, queueDetailsId]);

  const inferMetalFromProductGroupLabel = (label) => {
    const s = String(label || "").toLowerCase();
    if (s.includes("gold")) return "Gold";
    if (s.includes("silver")) return "Silver";
    if (s.includes("platinum")) return "Platinum";
    return "";
  };

  const seedProductGroupsFromRepairingEntries = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return;
    const byId = new Map();
    entries.forEach((row) => {
      const rawPid = row.product_group_id ?? row.product_group;
      const pid =
        rawPid != null &&
          rawPid !== "" &&
          Number.isFinite(Number(rawPid)) &&
          Number(rawPid) > 0
          ? rawPid
          : null;
      if (pid == null) return;
      const id = Number(pid);
      if (!Number.isFinite(id) || id <= 0) return;
      if (byId.has(id)) return;
      const name = String(row.product_group_name || "").trim();
      const label = name || String(id);
      byId.set(id, {
        value: id,
        label,
        short_code: row.short_code || "",
        purity: "",
        metal: inferMetalFromProductGroupLabel(label),
      });
    });
    const list = Array.from(byId.values());
    if (list.length === 0) return;

    productGroupOptionsCacheRef.current = mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      list,
    );
  };

  const getProductGroupLabelFromCache = (pgId, nameHint) => {
    const name = String(nameHint || "").trim();
    if (name) return name;
    if (pgId == null || pgId === "") return "";
    const cached = getProductGroupOptionsList().find(
      (pg) => String(pg.value) === String(pgId),
    );
    return cached?.label || cached?.name || "";
  };

  const applyPoolHeaderFromGroup = (group) => {
    if (!group) {
      setProductGroupPoolHeader(null);
      return;
    }
    setProductGroupPoolHeader({
      pcs: numPool(group.pool_pcs),
      nwt: numPool(group.pool_nwt),
    });
  };

  const getProductGroupOptionsList = () =>
    mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      dropdownData.productGroup,
    );

  const resolveProductGroupOption = async (id, labelHint) => {
    if (id != null && id !== "") {
      const cached = getProductGroupOptionsList().find(
        (pg) => String(pg.value) === String(id),
      );
      if (cached) return cached;
      const fetched = await fetchProductGroupOptionById(id);
      if (fetched) {
        productGroupOptionsCacheRef.current = mergeProductGroupOptions(
          productGroupOptionsCacheRef.current,
          [fetched],
        );
        return fetched;
      }
    }
    if (labelHint) {
      return getProductGroupOptionsList().find(
        (pg) =>
          pg.label === labelHint ||
          pg.name === labelHint ||
          pg.short_code === labelHint,
      );
    }
    return null;
  };

  const fetchProductGroupByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      return dropdownData.productGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        opts,
      );
      setDropdownData((prev) => ({
        ...prev,
        productGroup: opts,
      }));
      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
      if (opts.length > 0) {
        applyPoolHeaderFromGroup(opts[0]);
      }
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({ ...prev, productGroup: [] }));
      setProductGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    if (metalType === "Gold") {
      return rate / 10;
    } else if (metalType === "Silver") {
      return rate / 1000;
    }
    return rate;
  };

  // Calculate rate based on purity
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    // Extract the karat/purity value from string (e.g. "18K" -> 18 or "925" -> 925)
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate; // If purity is invalid, return base rate

    if (metalType === "Gold") {
      // Always show per gram: (masterGoldRate / 10) * (purity / 24)
      return (rate / 10) * (purityValue / 24);
    } else if (metalType === "Silver") {
      // For silver, use the formula: X Purity Silver Price per gram = (999 Silver Price × (X/999)) / 1000
      return (rate * purityValue) / 999 / 1000;
    }

    return rate; // Default case if metal type is not recognized
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "50px", value: (row) => row.no },
    {
      id: "productGroup",
      label: (
        <>
          Product Group 
        </>
      ),
      width: "120px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
          onFocus={(e) => handleFocus(row.id, "productGroup")}
          onKeyDown={(e) =>
            handleKeyDown(e, row.id, "productGroup", "productGroup")
          }
          className={`form-control form-control-sm ${buttonClass}`}
          autoComplete="off"
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "currentWeight",
      label: (
        <>
          Current Wt 
        </>
      ),
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.currentWeight}
          onChange={(e) => onChange(row.id, "currentWeight", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "expWeight",
      label: "Exp Wt",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.expWeight}
          onChange={(e) => onChange(row.id, "expWeight", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "issueDescription",
      label: "Issue Description",
      width: "200px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.issueDescription}
          onChange={(e) => onChange(row.id, "issueDescription", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "lType",
      label: "Lbr Type",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.lType}
          onChange={(e) => onChange(row.id, "lType", e.target.value)}
          onFocus={(e) => handleFocus(row.id, "lType")}
          onKeyDown={(e) => handleKeyDown(e, row.id, "lType", "lType")}
          className={`form-control form-control-sm ${buttonClass}`}
          autoComplete="off"
        />
      ),
    },
    {
      id: "lbrPercentage",
      label: "Lbr %",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.lbrPercentage}
          onChange={(e) => onChange(row.id, "lbrPercentage", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "Lbr Rate",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "lbrAmount",
      label: "Approx Lbr Amount",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.lbrAmount}
          onChange={(e) => onChange(row.id, "lbrAmount", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onChange(row.id, "image", e.target.files[0])}
        />
      ),
    },
    {
      id: "otherAmount",
      label: "Approx Other Amount",
      width: "120px",
      value: (row, onChange) => (
        <div className="position-relative">
          <input
            type="number"
            step="0.01"
            value={row.otherAmount}
            onChange={(e) => onChange(row.id, "otherAmount", e.target.value)}
            onDoubleClick={(e) => handleOtherAmountInteraction(row.id, e)}
            onKeyDown={(e) => handleOtherAmountInteraction(row.id, e)}
            onClick={(e) => handleOtherAmountInteraction(row.id, e)}
            className={`form-control form-control-sm ${buttonClass}`}
            style={{ cursor: "pointer" }}
          />
          <div
            id={`oth-amt-tooltip-${row.id}`}
            ref={(el) =>
              (tooltipRefs.current[`oth-amt-tooltip-${row.id}`] = el)
            }
            className="tooltip"
            style={{
              position: "absolute",
              top: "-35px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#333",
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              whiteSpace: "nowrap",
              zIndex: 1000,
              display: "none",
            }}
          >
            Double-click or press Enter twice to add charges
          </div>
        </div>
      ),
    },
    {
      id: "totalAmount",
      label: "Approx Total Amount",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="number"
          step="0.01"
          value={row.totalAmount}
          onChange={(e) => onChange(row.id, "totalAmount", e.target.value)}
          className={`form-control form-control-sm ${buttonClass}`}
          readOnly
        />
      ),
    },
  ];

  const fetchPaymentTypes = async ({ force = false } = {}) => {
    try {
      const response = await requestBankAccountsForRepairing({ force });

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankDetails = response.data.bank_accounts.map((bank) => ({
          id: bank.id,
          bankRegisterId: bank.bank_register_id ?? bank.register_id ?? bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance:
            bank.available_balance !== null &&
              bank.available_balance !== undefined
              ? bank.account_type === "metal"
                ? `${bank.available_balance} gm`
                : `₹${bank.available_balance}`
              : bank.account_type === "metal"
                ? "0 gm"
                : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.invoice === 1,
          useForPOS: bank.pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);
      } else if (response?.success && !response.data?.bank_accounts?.length) {
        setBankAccounts([]);
        setPaymentTypes([]);
      } else if (response && response.success === false) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details"
      );
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  // Prefill flow for Repairing Sales when navigating with repairingOrderId
  const isEditing = Boolean(repairingOrderId);
  useEffect(() => {
    if (!repairingOrderId) return;

    const fetchRepairingSaleData = async () => {
      setIsLoadingOrderData(true);
      try {
        const showRes = await requestRepairingOrderShowForEdit(
          repairingOrderId,
          { isQuote: isQuoteProp },
        );

        let order = null;
        if (isErpApiSuccess(showRes)) {
          const payload = showRes.data ?? showRes ?? {};
          const details = payload.repairing_order_details ?? payload;
          if (Array.isArray(details)) {
            order =
              details.find((o) => String(o.id) === String(repairingOrderId)) ||
              details[0] ||
              null;
          } else if (details && typeof details === "object" && !Array.isArray(details)) {
            order =
              details.repairing_order_entry || details.entries || details.items
                ? details
                : payload.repairing_order || details;
          }
          if (
            !order &&
            payload.id != null &&
            Array.isArray(payload.repairing_order_entry)
          ) {
            order = payload;
          }
        }
        if (!order) return;

        // Prefill customer by user_id/customer_id if available in userss.
        const customerIdFromOrder = order.user_id ?? order.customer_id;
        const user = userss.find(
          (u) => String(u.id) === String(customerIdFromOrder),
        );
        if (user) {
          setSelectedCustomerId(user.id);
          setCustomerMobile(user.mobile || "");
          setCustomerMobileSearchText(user.mobile || "");
          setCustomerName(user.fullName || user.name || "");
          setCustomerNameSearchText(user.fullName || user.name || "");
          setCustomerAddress(user.address || "");
          setCustomerCity(user.city || "");
          setCustomerDetails({
            email: user.email || "",
            birthDate: user.birthDate || "",
            anniversaryDate: user.anniversaryDate || "",
            companyName: user.companyName || "",
            companyAddress: user.companyAddress || "",
            gstNumber: user.gstNumber || "",
            aadharNo: user.aadharNo || "",
            panNo: user.panNo || "",
            maritalStatus: user.maritalStatus || "",
            balance: user.balance || "",
            saleBalance: user.saleBalance || "",
            reference: user.reference || "",
            state: user.state || "",
          });
        } else {
          // Fallback for API payloads that only send customer_name/mobile
          // without a full user object in userss.
          if (customerIdFromOrder != null) {
            setSelectedCustomerId(customerIdFromOrder);
          }
          setCustomerMobile(order.customer_mobile || "");
          setCustomerMobileSearchText(order.customer_mobile || "");
          const fallbackName =
            order.customer_name || order.user_name || order.name || "";
          setCustomerName(fallbackName);
          setCustomerNameSearchText(fallbackName);
        }

        // Prefill expected delivery
        if (order.exp_delivery || order.expected_delivery) {
          try {
            const d = new Date(order.exp_delivery || order.expected_delivery);
            if (!isNaN(d)) setExpDate(d.toISOString().split("T")[0]);
          } catch (_) { }
        }

        const loadedFollowup =
          order.followup_date || order.follow_up_date || "";
        if (loadedFollowup) {
          try {
            const fd = new Date(loadedFollowup);
            setFollowUpDate(
              !isNaN(fd) ? fd.toISOString().split("T")[0] : String(loadedFollowup).slice(0, 10),
            );
          } catch (_) {
            setFollowUpDate(String(loadedFollowup).slice(0, 10));
          }
        } else {
          setFollowUpDate("");
        }

        setNote(String(order.note || order.notes || "").slice(0, 70));

        const billTypeId =
          order.bill_type_id ?? order.billTypeId ?? order.billtype_id;
        const billTypeName =
          order.bill_type_name ?? order.billTypeName ?? order.billtype_name;
        const normalizedBillTypeName = String(billTypeName || "").trim();
        const matchedBillType =
          billTypes.find(
            (item) =>
              (billTypeId != null &&
                String(item.id) === String(billTypeId)) ||
              (normalizedBillTypeName &&
                item.name.toLowerCase() === normalizedBillTypeName.toLowerCase()),
          ) || null;
        if (matchedBillType) {
          selectedBillTypeIdRef.current = String(matchedBillType.id);
          setSelectedBillTypeId(String(matchedBillType.id));
          setSelectedBillTypeName(matchedBillType.name || "GST");
        } else {
          if (billTypeId != null && String(billTypeId).trim() !== "") {
            selectedBillTypeIdRef.current = String(billTypeId);
            setSelectedBillTypeId(String(billTypeId));
          }
          if (normalizedBillTypeName) {
            setSelectedBillTypeName(normalizedBillTypeName);
          }
        }

        // Map repairing entries to table rows for UI
        const entries = Array.isArray(order.repairing_order_entry)
          ? order.repairing_order_entry
          : Array.isArray(order.entries)
            ? order.entries
            : Array.isArray(order.items)
              ? order.items
              : [];

        seedProductGroupsFromRepairingEntries(entries);

        const mappedRows = entries.map((item, idx) => {
          // Approx Total = labor amount + other amount (not lbr_rate / API total_amount).
          // Fixed rows often have lbr_rate=0 with labor_charge set; API total_amount
          // can be 0 or wrongly equal to lbr_rate.
          const loadedLbrAmt =
            parseFloat(
              item.lbr_amount ?? item.labor_charge ?? item.labor_amount ?? 0,
            ) || 0;
          const loadedOthAmt =
            parseFloat(item.other_amount ?? item.other_charge ?? 0) || 0;
          const totalAmt = (loadedLbrAmt + loadedOthAmt).toFixed(2);

          const rawImagePath =
            item.image ||
            item.design_photos ||
            item.design_photo ||
            item.photo ||
            item.img ||
            item.image_path ||
            "";

          return {
            id: idx + 1,
            originalEntryId: item.id || null,
            data: COLUMNS.map((col) => {
              const id = col.id;
              let value = "";
              if (id === "no") value = idx + 1;
              else if (id === "productGroup") {
                const pgId =
                  item.product_group || item.product_group_id || null;
                const label = getProductGroupLabelFromCache(
                  pgId,
                  item.product_group_name,
                );
                const cell = { id, value: label };
                if (pgId) {
                  cell.productGroupId = pgId;
                }
                return cell;
              }
              if (id === "pcs") value = item.pcs || "";
              else if (id === "currentWeight")
                value = item.current_weight || "";
              else if (id === "expWeight")
                value = item.exp_weight ?? item.expected_weight ?? "";
              else if (id === "issueDescription")
                value = item.issue_description || item.description || "";
              else if (id === "lType") value = item.lbr_type || "";
              else if (id === "lbrPercentage")
                value = item.lbr_percentage || "";
              else if (id === "lbrRate")
                value = item.lbr_rate ?? item.labor_charge ?? "";
              else if (id === "lbrAmount")
                value = item.lbr_amount ?? item.labor_charge ?? "";
              else if (id === "image") {
                value = rawImagePath || "";
                return {
                  id,
                  value,
                  imageFile: null,
                  imagePreview: rawImagePath ? getNodeImageUrl(rawImagePath) : null,
                };
              }
              else if (id === "otherAmount") {
                value = item.other_amount ?? item.other_charge ?? "";
                // Map charges to otherAmountEntries
                const otherAmountEntries = mapApiChargesToOtherAmountEntries(
                  item.repairing_order_charges,
                );
                return { id, value, otherAmountEntries };
              } else if (id === "totalAmount") value = totalAmt;
              return { id, value };
            }),
          };
        });

        setTableData(
          mappedRows.length > 0 ? mappedRows : [{ id: 1, data: initialRowData }]
        );
        const repairingPayments = mapApiPaymentsToUiRows(order.payments, {
          includeBankRegister: true,
        });
        if (repairingPayments) {
          setPaymentRows(repairingPayments);
        }
      } catch (err) {
        console.error("Error loading repairing sale data:", err);
      } finally {
        setIsLoadingOrderData(false);
      }
    };

    fetchRepairingSaleData();
  }, [repairingOrderId]);

  const [karigars, setKarigars] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Fetch karigars only when searching (not on pageload)
  const fetchKarigars = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";
    if (!searchText || searchText.length < 3) return [];

    try {
      const response = await listSuppliers({ search: searchText, status: 1 });
      const list =
        response?.success && Array.isArray(response.data?.suppliers)
          ? response.data.suppliers
          : [];

      if (list.length > 0) {
        const fetchedKarigars = list.map((karigar) => {
          return {
            id: karigar.id,
            fullName: karigar.name || "",
            companyName: karigar.company_name || "",
            shortCode: karigar.short_code || "",
            contactNumber: karigar.contact_number || "",
            email: karigar.email || "",
            address: karigar.address || "",
            city: karigar.city || "",
            gstNumber: karigar.gst_number || karigar.GST_number || "",
            status: karigar.status || "",
            isPrimary:
              karigar.primary_supplier === 1 ||
                karigar.primary_supplier === "1"
                ? 1
                : 0,
          };
        });

        fetchedKarigars.sort((a, b) => Number(b.id) - Number(a.id));
        setKarigars((prev) => {
          const byId = new Map(
            (Array.isArray(prev) ? prev : []).map((k) => [String(k.id), k]),
          );
          fetchedKarigars.forEach((k) => byId.set(String(k.id), k));
          return Array.from(byId.values()).sort(
            (a, b) => Number(b.id) - Number(a.id),
          );
        });
        karigarSearchCacheRef.current.add(searchText.toLowerCase());
        return fetchedKarigars;
      }
      return [];
    } catch (error) {
      console.error("Error fetching karigar details:", error);
      toast.error(error.response?.data?.message || "Failed to fetch karigars");
      return [];
    }
  };

  // Karigar states
  const [assignKarigarMode, setAssignKarigarMode] = useState("later");
  const [sameKarigarForAll, setSameKarigarForAll] = useState(false);
  const [selectedKarigarId, setSelectedKarigarId] = useState(null);
  const [karigarName, setKarigarName] = useState("");
  const [karigarNameSearchText, setKarigarNameSearchText] = useState("");
  const [karigarNameDropdownActive, setKarigarNameDropdownActive] =
    useState(false);
  const [karigarNameHighlightedIndex, setKarigarNameHighlightedIndex] =
    useState(0);
  const karigarNameDropdownRef = useRef(null);
  const karigarNameInputRef = useRef(null);
  const karigarNameItemsRef = useRef([]);

  // Karigar search — only when typing 3+ chars (no pageload list)
  useEffect(() => {
    if (selectedKarigarId) return;
    const nextSearch = String(karigarNameSearchText || "").trim();
    if (
      nextSearch.length < 3 ||
      karigarSearchCacheRef.current.has(nextSearch.toLowerCase())
    ) {
      return;
    }
    void fetchKarigars(nextSearch);
  }, [karigarNameSearchText, selectedKarigarId]);

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    design: [],
    lType: ["Fixed", "Pcs", "Exp Wt"], // Updated Lbr Type options
    itRateOn: ["NWt", "GWt"], // Adding new options for ItRateOn
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Add refs for tooltip functionality
  const tooltipRefs = useRef({});
  const tooltipTimeoutRef = useRef(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Columns that should have dropdown functionality
  const dropdownColumns = ["productGroup", "lType", "rateOn", "itRateOn"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: "",
    // Track selected product group id alongside display label
    ...(col.id === "productGroup" ? { productGroupId: null } : {}),
    // Add imageFile property for image column
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) =>
      pruneLineFieldErrors(
        prev,
        collectLineItemFieldErrors(
          tableData,
          REPAIRING_ORDER_LINE_ERROR_OPTIONS,
        ),
      ),
    );
  }, [tableData]);

  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [productGroupPoolHeader, setProductGroupPoolHeader] = useState(null);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const productGroupOptionsCacheRef = useRef([]);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const tableContainerRef = useRef(null);
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: null,
    zoomLevel: 1,
  });

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  const [billTypes, setBillTypes] = useState([]);
  const [selectedBillTypeId, setSelectedBillTypeId] = useState("");
  const [selectedBillTypeName, setSelectedBillTypeName] = useState("GST");
  const selectedBillTypeIdRef = useRef("");
  const [billDate, setBillDate] = useState("");
  // Add expDate state — default to today (same as order date)
  const [expDate, setExpDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerMobileInvalid, setCustomerMobileInvalid] = useState(false);
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const customerMobileDropdownRef = useRef(null);
  const customerMobileInputRef = useRef(null);
  const customerMobileItemsRef = useRef([]);

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");
  const [customerNameDropdownActive, setCustomerNameDropdownActive] =
    useState(false);
  const [customerNameHighlightedIndex, setCustomerNameHighlightedIndex] =
    useState(0);
  const customerNameDropdownRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerNameItemsRef = useRef([]);

  // Customer search — only when typing 3+ chars (no pageload list)
  useEffect(() => {
    if (selectedCustomerId) return;

    const nextSearch = [customerMobileSearchText, customerNameSearchText]
      .map((value) => (value ? String(value).trim() : ""))
      .find(
        (value) =>
          value.length >= 3 &&
          !customerSearchCacheRef.current.has(value.toLowerCase()),
      );

    if (!nextSearch) return;
    void fetchUserss(nextSearch);
  }, [
    customerMobileSearchText,
    customerNameSearchText,
    selectedCustomerId,
  ]);

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");

  // Add new state for customer details modal
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
    area: "",
  });

  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();

  // Add new state for tracking if customer is being added manually
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);

  // Add Customer Form States
  // const [fullName, setFullName] = useState("");
  // const [mobile, setMobile] = useState("");
  // const [email, setEmail] = useState("");
  // const [city, setCity] = useState("");
  // const [address, setAddress] = useState("");
  // const [birthDate, setBirthDate] = useState("");
  // const [maritalStatus, setMaritalStatus] = useState("");
  // const [anniversaryDate, setAnniversaryDate] = useState("");
  // const [openingBalance, setOpeningBalance] = useState("");
  // const [pendingBalance, setPendingBalance] = useState("");
  // const [companyName, setCompanyName] = useState("");
  // const [companyAddress, setCompanyAddress] = useState("");
  // const [gstNumber, setGstNumber] = useState("");
  // const [status, setStatus] = useState("active");
  // const [showAddModal, setShowAddModal] = useState(false);

  // Mock customer data - replace with real API data
  const CUSTOMERS = userss.map((user) => ({
    id: user.id,
    name: user.fullName,
    fullName: user.fullName,
    mobile: user.mobile,
    email: user.email,
    city: user.city,
    address: user.address,
    birthDate: user.birthDate,
    maritalStatus: user.maritalStatus,
    anniversaryDate: user.anniversaryDate,
    balance: user.balance,
    saleBalance: user.saleBalance,
    companyName: user.companyName,
    companyAddress: user.companyAddress,
    gstNumber: user.gstNumber,
    aadharNo: user.aadharNo,
    panNo: user.panNo,
    reference: user.reference,
    state: user.state,
  }));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    selectedBillTypeIdRef.current = selectedBillTypeId;
  }, [selectedBillTypeId]);

  const applyBillTypeSelection = (billType) => {
    const id = billType?.id != null ? String(billType.id) : "";
    const name = String(billType?.name || "").trim();
    selectedBillTypeIdRef.current = id;
    setSelectedBillTypeId(id);
    setSelectedBillTypeName(name || "GST");
  };

  useEffect(() => {
    const fetchBillTypeOptions = async () => {
      try {
        const response = await requestBillTypesForRepairing();
        const rows = Array.isArray(response?.data?.bill_types)
          ? response.data.bill_types
          : [];
        if (!isErpApiSuccess(response) || !Array.isArray(rows)) return;

        const mapped = rows.map((row) => ({
          id: row?.id,
          name: String(row?.name || "").trim(),
        }));
        setBillTypes(mapped);

        if (!selectedBillTypeIdRef.current) {
          const gstType =
            mapped.find((item) => item.name.toLowerCase() === "gst") ||
            mapped[0];
          if (gstType) {
            applyBillTypeSelection(gstType);
          }
        }
      } catch (error) {
        console.error("Error fetching bill types:", error);
      }
    };

    fetchBillTypeOptions();
  }, []);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
    setExpDate(today); // Default Exp Delivery to today
  }, []);

  // useEffect(() => {
  //   const list = Array.isArray(userss) ? userss : [];
  //   if (!list.length) return;
  //   const mobile = (customerMobileSearchText || customerMobile || "").trim();
  //   const name = (customerNameSearchText || customerName || "")
  //     .trim()
  //     .toLowerCase();
  //   let match = null;
  //   if (mobile) {
  //     match =
  //       list.find((u) => String(u.mobile || "").trim() === String(mobile)) ||
  //       null;
  //   }
  //   if (!match && name) {
  //     const candidates = list.filter((u) =>
  //       String(u.fullName || u.name || "")
  //         .toLowerCase()
  //         .includes(name)
  //     );
  //     if (candidates.length === 1) {
  //       match = candidates[0];
  //     }
  //   }
  //   if (match) {
  //     setSelectedCustomerId(match.id);
  //     setCustomerName(match.fullName || match.name || "");
  //     setCustomerNameSearchText(match.fullName || match.name || "");
  //     setCustomerMobile(match.mobile || "");
  //     setCustomerMobileSearchText(match.mobile || "");
  //     setCustomerCity(match.city || "");
  //     setCustomerAddress(match.address || "");
  //     setCustomerDetails((prev) => ({
  //       ...prev,
  //       email: match.email || "",
  //       birthDate: match.birthDate || "",
  //       anniversaryDate: match.anniversaryDate || "",
  //       companyName: match.companyName || "",
  //       companyAddress: match.companyAddress || "",
  //       gstNumber: match.gstNumber || "",
  //       aadharNo: match.aadharNo || "",
  //       panNo: match.panNo || "",
  //       maritalStatus: match.maritalStatus || "",
  //       balance: match.balance || "",
  //       saleBalance: match.saleBalance || "",
  //       reference: match.reference || "",
  //       state: match.state || "",
  //       area: match.area || "",
  //     }));
  //   }
  // }, [
  //   userss,
  //   customerMobileSearchText,
  //   customerMobile,
  //   customerNameSearchText,
  //   customerName,
  // ]);

  // Update dropdown position after API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      // Use setTimeout to ensure DOM is updated before calculating position
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };
  // Add handler for expDate
  const handleExpDateChange = (e) => {
    setExpDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const chargeTypes = useOtherChargeOptions();


  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = chargeTypes.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Sync pool balance when navigating product group dropdown
  useEffect(() => {
    if (
      activeDropdown.type !== "productGroup" ||
      activeDropdown.row === null ||
      activeDropdown.col === null
    ) {
      return;
    }
    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    const filtered = getFilteredItems(cell?.value, "productGroup");
    if (filtered[highlightedIndex]) {
      applyPoolHeaderFromGroup(filtered[highlightedIndex]);
    }
  }, [highlightedIndex, activeDropdown, dropdownData.productGroup]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container for dropdown
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  // Function to calculate Labor Amount using Lbr %
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    masterRate,
    metalType
  ) => {
    return (
      (lbrPercentage / 100) *
      baseWeight *
      calculatePerGramRate(masterRate, metalType)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value) => {
    const newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;

    // Store previous value to check if it has changed
    const previousValue = cell.value;

    // If the changed column is lbrType
    if (id === "lbrType") {
      cell.value = value;

      // If Fixed is selected, clear lbrPercentage and lbrRate
      if (value === "Fixed") {
        const lbrPercentageCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrPercentage"
        );
        const lbrRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "lbrRate"
        );

        if (lbrPercentageCell) {
          lbrPercentageCell.value = "";
        }
        if (lbrRateCell) {
          lbrRateCell.value = "";
        }
      }

      setTableData(newTableData);
      return;
    }

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      design: /^[a-zA-Z0-9\s\-\_]*$/, // Allow alphanumeric, spaces, hyphens, underscores
      productType: /^.*$/, // Allow any string for product type
      size: /^\d*$/, // Only numbers
      pcs: /^\d*$/, // Only numbers
      lbrPercentage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      mrp: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      // console.log(`Validation failed for ${id}: ${value}`);
      return; // Don't update if validation fails
    }

    // Handle productGroup: fetch options when user types 3+ characters
    if (id === "productGroup") {
      cell.value = value;
      setTableData(newTableData);

      // Trigger API call when user types 3+ characters
      if (value && value.length >= 3) {
        // Only reset if the new value doesn't start with the last searched prefix
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false); // Reset flag before API call
          fetchProductGroupByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, productGroup: [] }));
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
        setProductGroupPoolHeader(null);
      }

      // Check if the value matches any existing product group
      const matchingProductGroup = getProductGroupOptionsList().find(
        (pg) =>
          pg.label === value ||
          pg.name === value ||
          pg.short_code === value,
      );

      if (!matchingProductGroup) {
        cell.productGroupId = null;
      } else {
        // If we found a match, set the productGroupId
        cell.productGroupId = matchingProductGroup.value;
      }
      return;
    }

    cell.value = value;

    // Handle karigar selection (supports id or name value)
    if (id === "karigarName") {
      const selectedKarigar = karigars.find(
        (karigar) =>
          String(karigar.id) === String(value) ||
          karigar.fullName === value ||
          (karigar.name && karigar.name === value)
      );
      if (selectedKarigar) {
        // Persist selected id for API and show readable text in cell
        cell.karigarId = selectedKarigar.id;
        cell.value = selectedKarigar.companyName
          ? `${selectedKarigar.fullName} (${selectedKarigar.companyName})`
          : selectedKarigar.fullName || selectedKarigar.name || "";
      } else {
        // If user cleared the field
        cell.karigarId = null;
      }
    }

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt"
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        if (netWtCell) {
          netWtCell.value = netWt.toString();

          // Labor calculations are handled separately
        }
      }
    } else if (
      id === "netWt" ||
      id === "itRate" ||
      id === "grWt" ||
      id === "itRateOn"
    ) {
      // Calculate Item Amount when Net Weight, Gross Weight, Item Rate, or Item Rate On changes
      const netWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "netWt"
      );
      const grWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRate"
      );
      const itRateOnCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRateOn"
      );

      if (netWtCell && itRateCell && itRateCell.value) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const itRate = parseFloat(itRateCell.value) || 0;
        const itRateOn = itRateOnCell?.value || "NWt"; // Default to NWt if not specified

        // Determine which weight to use based on itRateOn
        let baseWeight = netWt; // Default to Net Weight
        if (itRateOn === "GWt") {
          baseWeight = grWt;
        }

        // Labor calculations are handled separately
      }
    } else if (id === "lType") {
      // Handle LBR type change
      const lTypeCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lType"
      );
      if (lTypeCell) {
        lTypeCell.value = value;
      }
    } else if (
      id === "lbrAmount" ||
      id === "otherAmount" ||
      id === "totalAmount"
    ) {
      // Recalculate total when any of the amount fields change
      // console.log("Recalculating total for:", id, "value:", value);
      calculateTotalAmount(newTableData, rowIndex);
    }

    // Handle LBR percentage and rate calculations
    if (id === "lType" || id === "lbrPercentage" || id === "lbrRate") {
      // Track which labor field was last changed
      if (id === "lbrPercentage") {
        newTableData[rowIndex].lastChangedLaborField = id;
      }
      if (id === "lbrRate") {
        newTableData[rowIndex].lastChangedLaborField = id;
      }
      handleLaborCalculations(newTableData, rowIndex);
    }

    // Track last changed labor field when lbrRate updates
    // (covered in the block above)

    setTableData(newTableData);

    // Force a re-render of the total calculation
    if (id === "lbrAmount" || id === "otherAmount") {
      setTimeout(() => {
        calculateTotalAmount(newTableData, rowIndex);
      }, 100);
    }
  };

  // New function to calculate total amount
  // Calculate total amount for repairing order (simplified)
  const calculateTotalAmount = (tableData, rowIndex) => {
    const lbrAmountCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmount"
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "otherAmount"
    );
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmount"
    );

    const lbrAmount = parseFloat(lbrAmountCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;
    const totalAmt = (lbrAmount + othAmt).toFixed(2);

    // Update the total amount cell
    if (totalAmtCell) {
      totalAmtCell.value = totalAmt;
    }

    return totalAmt;
  };

  // Handle image upload
  const handleImageUpload = async (field, rowIndex) => {
    // console.log("Field:", field);

    // Create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview and store the file in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field; // Store the actual file object
            imageCell.value = null; // Clear any previous uploaded image name
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Open image modal
  const openImageModal = (imageUrl) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    setImageModal({
      isOpen: true,
      imageUrl,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                // Find and remove the image
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      cell.imagePreview === imageModal.imageUrl
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    setTableData((prevData) => {
                      const updatedData = [...prevData];
                      const imageCell = updatedData[rowIndex].data.find(
                        (c) => c.id === "image"
                      );
                      if (imageCell) {
                        imageCell.value = "";
                        imageCell.imageFile = null;
                        imageCell.imagePreview = null;
                      }
                      return updatedData;
                    });
                    closeImageModal();
                  }
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: "", // Remove default value for itRateOn
          // Add imageFile property for image column
          ...(col.id === "image"
            ? { imageFile: null, imagePreview: null }
            : {}),
        })),
      },
    ]);

    // Keep new row + Total in view; allow adding again after a short delay
    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
      window.isAddingRow = false;
    }, 150);
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    // Ensure we're passing the label or value, not the entire object
    const value =
      typeof selectedItem === "object" ? selectedItem.label : selectedItem;

    // If selecting product group, set the productGroupId first
    if (COLUMNS[colIndex]?.id === "productGroup") {
      setTableData((prev) => {
        const copy = [...prev];
        const cell = copy[rowIndex]?.data[colIndex];
        if (cell) {
          const idVal =
            typeof selectedItem === "object" ? selectedItem.value : null;
          cell.productGroupId = idVal;
          cell.value = value; // Set the value directly to avoid clearing productGroupId
        }
        return copy;
      });
    } else {
      handleInputChange(rowIndex, colIndex, value);
    }
    setActiveDropdown({ row: null, col: null, type: null });

    const columnId = COLUMNS[colIndex]?.id;

    if (columnId === "productGroup" && typeof selectedItem === "object") {
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        [selectedItem],
      );
      applyPoolHeaderFromGroup(selectedItem);
    }

    // Check if this is a tagNo field and add a new row if it is

    // Calculate purity-based rate when product group is selected
    if (columnId === "productGroup") {
      // Find the selected product group object to get its purity
      const selectedProductGroup = getProductGroupOptionsList().find(
        (pg) => pg.label === value || pg.name === value,
      );
      if (selectedProductGroup && selectedProductGroup.purity) {
        // Calculate the purity-based rate
        const baseRate =
          selectedProductGroup.metal === "Gold"
            ? masterGoldRate
            : masterSilverRate;
        const rate24k = calculatePerGramRate(
          baseRate,
          selectedProductGroup.metal
        );
        const purityBasedRate = calculatePurityBasedRate(
          selectedProductGroup.purity,
          rate24k,
          selectedProductGroup.metal
        );

        // Find the itRate column index
        const itRateColIndex = COLUMNS.findIndex((col) => col.id === "itRate");
        if (itRateColIndex !== -1) {
          // Update the itRate with the calculated purity-based rate
          handleInputChange(
            rowIndex,
            itRateColIndex,
            purityBasedRate.toFixed(2)
          );
        }
      }
    }

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "lType" || columnType === "itRateOn") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText)
      );
    }

    // Special case: productGroup — API search results (no client-side re-filter)
    if (columnType === "productGroup") {
      return dropdownData[columnType] || [];
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return item.toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);
    // Reset refs array
    dropdownItemsRef.current = [];

    // Calculate max width based on content
    const contentWidths = filtered.map((item) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";

      // Safely get the label
      const itemLabel = typeof item === "object" ? item.label || "" : item;
      tempSpan.textContent = itemLabel;

      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0
        ? Math.max(...contentWidths)
        : dropdownPosition.width;

    // Special styling for productGroup dropdown - match AddGirvi.jsx pattern
    const isProductGroup = activeDropdown.type === "productGroup";
    // For productGroup: use 5 items with 40px height each (matching AddGirvi.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = isProductGroup ? 40 : 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Calculate width for dropdowns - match AddGirvi.jsx pattern
    const adjustedWidth = isProductGroup
      ? Math.max(200, dropdownPosition.width)
      : Math.max(dropdownPosition.width, maxContentWidth + 20);

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${adjustedWidth}px`,
          maxHeight: isProductGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          height: isProductGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {isProductGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? productGroupOptionDisplayName(item)
                    : item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isProductGroup ? "normal" : "nowrap",
                    wordWrap: isProductGroup ? "break-word" : "normal",
                    overflow: isProductGroup ? "visible" : "hidden",
                    textOverflow: isProductGroup ? "clip" : "ellipsis",
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(idx);
                    if (isProductGroup) applyPoolHeaderFromGroup(item);
                  }}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    // If this is a dropdown column, show the dropdown
    // For productGroup, only show dropdown if user has typed 3+ characters and we have results
    const columnType = getColumnType(colIndex);
    if (isDropdownColumn(columnType)) {
      if (columnType === "productGroup") {
        // For productGroup, only show dropdown if user has typed 3+ characters AND we have results or API was called
        const productGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "productGroup"
        );
        const productGroupValue = (productGroupCell?.value || "")
          .toString()
          .trim();
        const hasEnoughChars = productGroupValue.length >= 3;
        if (!hasEnoughChars && productGroupValue) {
          const selected = getProductGroupOptionsList().find(
            (pg) =>
              pg.name === productGroupValue || pg.label === productGroupValue,
          );
          applyPoolHeaderFromGroup(selected);
        }
        const hasResults =
          getFilteredItems(productGroupValue, "productGroup").length > 0;

        // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
        if (hasEnoughChars && (hasResults || productGroupApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ characters
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "otherAmount")
        ?.otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "otherAmount")
        ?.value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
          ...entry,
          rateOn: entry.rateOn || "", // Ensure rateOn is initialized
        }))
        : currentValue
          ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get all required values
      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      const lTypeCell = row.data.find((cell) => cell.id === "lType");
      const lbrPercentageCell = row.data.find(
        (cell) => cell.id === "lbrPercentage"
      );
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");
      const itRateOnCell = row.data.find((cell) => cell.id === "itRateOn");
      const productGroupCell = row.data.find(
        (cell) => cell.id === "productGroup"
      );

      const grWt = parseFloat(grWtCell?.value) || 0;
      const lType = lTypeCell?.value;
      const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
      const lbrRate = parseFloat(lbrRateCell?.value) || 0;
      const itRate = parseFloat(itRateCell?.value) || 0;
      const itRateOn = itRateOnCell?.value;

      // Get metal type from product group
      const selectedProductGroup = getProductGroupOptionsList().find(
        (pg) => pg.label === productGroupCell?.value
      );
      const metalType = selectedProductGroup?.metal || "Gold";

      // Update Other Amount
      const othAmtCell = row.data.find((cell) => cell.id === "otherAmount");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      // Update Less Weight
      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      if (lsWtCell) {
        lsWtCell.value = formatLessWeight(totalLessWeight);
      }

      // Calculate and update Net Weight
      const netWeight = calculateNetWeight(grWt, totalLessWeight);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      // Labor calculations are handled separately

      // Calculate Labor Amount
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmount");
      if (lbrAmtCell) {
        let baseWeight = 0;
        if (lType === "NWt") {
          baseWeight = netWeight;
        } else if (lType === "GWt") {
          baseWeight = grWt;
        }

        let lbrAmt = 0;
        if (lbrPercentage > 0) {
          lbrAmt = calculateLbrAmtWithPercentage(
            baseWeight,
            lbrPercentage,
            itRate,
            metalType
          );
        } else if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        lbrAmtCell.value = lbrAmt.toFixed(2);
      }

      // Calculate Total Amount
      const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const totalAmt = (lbrAmt + othAmt).toFixed(2);

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  // Render the otherAmountModal with reduced width
  const renderOtherAmountModal = () => {
    if (!otherAmountModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={otherAmountModal.isOpen}
        onHide={() =>
          setOtherAmountModal((prev) => ({ ...prev, isOpen: false }))
        }
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Other Amount Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={`table-responsive overflow-hidden ${buttonClass}`}>
            <table
              className={`table text-nowrap mb-0 align-middle table-bordered ${tableClass} theme-header-table`}
            >
              <thead className="text-dark fs-4">
                <tr>
                  {[
                    "No.",
                    "Charge Type",
                    "Pcs",
                    "Ct",
                    "Wt",
                    "Con%",
                    "Rate On",
                    "Rate",
                    "Amount",
                  ].map((header, index) => (
                    <th
                      key={header}
                      className="px-1 py-1 text-center"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      {header}
                      <div
                        className="resize-handle"
                        style={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "5px",
                          cursor: "col-resize",
                          backgroundColor: "transparent",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startWidth =
                            e.currentTarget.parentElement.offsetWidth;
                          const column = header
                            .toLowerCase()
                            .replace(/[^a-z]/g, "");

                          const handleMouseMove = (e) => {
                            const diff = e.clientX - startX;
                            const newWidth = Math.max(50, startWidth + diff);
                            handleOtherAmountColumnWidthChange(
                              column,
                              newWidth
                            );
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener(
                              "mousemove",
                              handleMouseMove
                            );
                            document.removeEventListener(
                              "mouseup",
                              handleMouseUp
                            );
                          };

                          document.addEventListener(
                            "mousemove",
                            handleMouseMove
                          );
                          document.addEventListener("mouseup", handleMouseUp);
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {otherAmountModal.entries.map((entry, index) => (
                  <tr key={index} style={{ backgroundColor: "#ffffff" }}>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span style={{ fontSize: "0.85rem" }}>{index + 1}</span>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <InlineAutoCompleteInput
                        id={`entry-${index}`} // Add unique ID for existing entries
                        value={entry.chargeType}
                        chargeOptions={chargeTypes}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].chargeType = newValue;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          getSuggestions(newValue);
                        }}
                        onComplete={(suggestion) => {
                          setOtherAmountModal((prev) => {
                            const newEntries = [...prev.entries];
                            const current = newEntries[index] || {};
                            const next = applyOtherChargeToEntry(
                              current,
                              chargeTypes,
                              suggestion,
                            );
                            if (
                              next.rate !== current.rate ||
                              next.rateOn !== current.rateOn
                            ) {
                              next.amount = formatAmount(calculateAmount(next));
                            }
                            newEntries[index] = next;
                            return { ...prev, entries: newEntries };
                          });
                          setSuggestions([]);
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          if (entry.chargeType) {
                            getSuggestions(entry.chargeType);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => setSuggestions([]), 200);
                        }}
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.pcs}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              pcs: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.ct}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              ct: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.wt}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              wt: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.conPercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index] = {
                              ...newEntries[index],
                              conPercentage: value,
                            };
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div className="position-relative">
                        <input
                          type="text"
                          value={entry.rateOn}
                          onChange={(e) => {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].rateOn = e.target.value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }}
                          className={`form-control border-0 rounded-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          onFocus={(e) => {
                            e.target.style.backgroundColor = "#fff";
                            const rect = e.target.getBoundingClientRect();
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              // Calculate max width based on content
                              const maxWidth = Math.max(
                                ...rateOnOptions.map((item) => {
                                  const tempSpan =
                                    document.createElement("span");
                                  tempSpan.style.fontSize = "0.85rem";
                                  tempSpan.style.visibility = "hidden";
                                  tempSpan.style.position = "absolute";
                                  tempSpan.style.whiteSpace = "nowrap";
                                  tempSpan.textContent = item;
                                  document.body.appendChild(tempSpan);
                                  const width = tempSpan.offsetWidth;
                                  document.body.removeChild(tempSpan);
                                  return width;
                                }),
                                rect.width // minimum width is input width
                              );

                              dropdown.style.display = "block";
                              dropdown.style.position = "fixed";
                              dropdown.style.top = `${rect.bottom}px`;
                              dropdown.style.left = `${rect.left}px`;
                              dropdown.style.width = "auto";
                              dropdown.style.minWidth = `${rect.width}px`;
                              dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                              dropdown.style.zIndex = "9999";
                              // Reset highlighted index when opening dropdown
                              setHighlightedIndex(0);
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            setTimeout(() => {
                              const dropdown = e.target.nextElementSibling;
                              if (dropdown) {
                                dropdown.style.display = "none";
                              }
                            }, 200);
                          }}
                        />
                        <div
                          className="dropdown-menu shadow-sm"
                          style={{
                            display: "none",
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            position: "fixed",
                            zIndex: 9999,
                            border: "1px solid rgba(0,0,0,.15)",
                            borderRadius: "0.25rem",
                            padding: "0.5rem 0",
                            width: "auto",
                            minWidth: "100%",
                          }}
                        >
                          {rateOnOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes((entry.rateOn || "").toLowerCase())
                            )
                            .map((option, i) => (
                              <div
                                key={i}
                                className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                  }`}
                                style={{
                                  cursor: "pointer",
                                  padding: "0.25rem 1rem",
                                  fontSize: "0.85rem",
                                  backgroundColor:
                                    i === highlightedIndex
                                      ? "var(--theme-primary)"
                                      : option.toLowerCase() ===
                                        entry.rateOn.toLowerCase()
                                        ? "#f0f0f0"
                                        : "transparent",
                                  color:
                                    i === highlightedIndex ? "#fff" : "inherit",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={() => {
                                  const newEntries = [
                                    ...otherAmountModal.entries,
                                  ];
                                  newEntries[index].rateOn = option;
                                  setOtherAmountModal((prev) => ({
                                    ...prev,
                                    entries: newEntries,
                                  }));
                                  // Hide dropdown after selection
                                  const dropdown = document.querySelector(
                                    '.dropdown-menu[style*="display: block"]'
                                  );
                                  if (dropdown) {
                                    dropdown.style.display = "none";
                                  }
                                }}
                              >
                                {option}
                              </div>
                            ))}
                        </div>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.rate}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td
                      className="p-0 position-relative"
                      style={{ border: "1px solid #dee2e6" }}
                    >
                      <input
                        type="text"
                        value={entry.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].amount = value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {/* Empty row for new entries */}
                <tr style={{ backgroundColor: "#ffffff" }}>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "38px" }}
                    >
                      <span style={{ fontSize: "0.85rem" }}>
                        {otherAmountModal.entries.length + 1}
                      </span>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <InlineAutoCompleteInput
                      id="current-entry" // Add unique ID for current entry
                      value={otherAmountModal.currentEntry.chargeType}
                      chargeOptions={chargeTypes}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            chargeType: newValue,
                          },
                        }));
                        getSuggestions(newValue);
                      }}
                      onComplete={(suggestion) => {
                        setOtherAmountModal((prev) => {
                          const next = applyOtherChargeToEntry(
                            prev.currentEntry,
                            chargeTypes,
                            suggestion,
                          );
                          if (
                            next.rate !== prev.currentEntry.rate ||
                            next.rateOn !== prev.currentEntry.rateOn
                          ) {
                            next.amount = formatAmount(calculateAmount(next));
                          }
                          return { ...prev, currentEntry: next };
                        });
                        setSuggestions([]);
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        if (otherAmountModal.currentEntry.chargeType) {
                          getSuggestions(
                            otherAmountModal.currentEntry.chargeType
                          );
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        setTimeout(() => setSuggestions([]), 200);
                      }}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.pcs}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              pcs: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.ct}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              ct: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.wt}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              wt: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.conPercentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              conPercentage: value,
                            },
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        value={otherAmountModal.currentEntry.rateOn}
                        onChange={(e) => {
                          handleRateOnDropdown(null, e.target.value, true);
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            dropdown.style.display = "block";
                          }
                        }}
                        onKeyDown={(e) => handleRateOnKeyDown(e, null, true)}
                        className={`form-control border-0 rounded-0 ${buttonClass}`}
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          const rect = e.target.getBoundingClientRect();
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            // Calculate max width based on content
                            const maxWidth = Math.max(
                              ...rateOnOptions.map((item) => {
                                const tempSpan = document.createElement("span");
                                tempSpan.style.fontSize = "0.85rem";
                                tempSpan.style.visibility = "hidden";
                                tempSpan.style.position = "absolute";
                                tempSpan.style.whiteSpace = "nowrap";
                                tempSpan.textContent = item;
                                document.body.appendChild(tempSpan);
                                const width = tempSpan.offsetWidth;
                                document.body.removeChild(tempSpan);
                                return width;
                              }),
                              rect.width // minimum width is input width
                            );

                            dropdown.style.display = "block";
                            dropdown.style.position = "fixed";
                            dropdown.style.top = `${rect.bottom}px`;
                            dropdown.style.left = `${rect.left}px`;
                            dropdown.style.width = "auto";
                            dropdown.style.minWidth = `${rect.width}px`;
                            dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                            dropdown.style.zIndex = "9999";
                            // Reset highlighted index when opening dropdown
                            setHighlightedIndex(0);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => {
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              dropdown.style.display = "none";
                            }
                          }, 200);
                        }}
                      />
                      <div
                        className="dropdown-menu shadow-sm"
                        style={{
                          display: "none",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#fff",
                          position: "fixed",
                          zIndex: 9999,
                          border: "1px solid rgba(0,0,0,.15)",
                          borderRadius: "0.25rem",
                          padding: "0.5rem 0",
                          width: "auto",
                          minWidth: "100%",
                        }}
                      >
                        {rateOnOptions
                          .filter((option) =>
                            option
                              .toLowerCase()
                              .includes(
                                (
                                  otherAmountModal.currentEntry.rateOn || ""
                                ).toLowerCase()
                              )
                          )
                          .map((option, i) => (
                            <div
                              key={i}
                              className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                }`}
                              style={{
                                cursor: "pointer",
                                padding: "0.25rem 1rem",
                                fontSize: "0.85rem",
                                backgroundColor:
                                  i === highlightedIndex
                                    ? "var(--theme-primary)"
                                    : option.toLowerCase() ===
                                      otherAmountModal.currentEntry.rateOn.toLowerCase()
                                      ? "#f0f0f0"
                                      : "transparent",
                                color:
                                  i === highlightedIndex ? "#fff" : "inherit",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={() => setHighlightedIndex(i)}
                              onMouseDown={() => {
                                handleRateOnDropdown(null, option, true);
                                // Hide dropdown after selection
                                const dropdown = document.querySelector(
                                  '.dropdown-menu[style*="display: block"]'
                                );
                                if (dropdown) {
                                  dropdown.style.display = "none";
                                }
                              }}
                            >
                              {option}
                            </div>
                          ))}
                      </div>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td
                    className="p-0 position-relative"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              amount: value,
                            },
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Save and close modal on Enter if amount is valid
                        if (e.key === "Enter") {
                          const value = otherAmountModal.currentEntry.amount;
                          if (value && !isNaN(value) && Number(value) > 0) {
                            e.preventDefault();

                            // Add current entry to entries
                            const finalEntries = [
                              ...otherAmountModal.entries,
                              otherAmountModal.currentEntry,
                            ];

                            // Calculate total amount
                            const totalAmount = finalEntries.reduce(
                              (sum, entry) => sum + Number(entry.amount || 0),
                              0
                            );

                            // Calculate total less weight
                            const totalLessWeight = finalEntries.reduce(
                              (sum, entry) => {
                                const lessWeight = calculateLessWeight(entry);
                                return (
                                  sum + (isNaN(lessWeight) ? 0 : lessWeight)
                                );
                              },
                              0
                            );

                            // Update table data with both values
                            setTableData((prev) => {
                              const newData = [...prev];
                              const row = newData[otherAmountModal.rowIndex];

                              if (!row || !row.data) return prev;

                              // Update Other Amount (rounded)
                              const othAmtCell = row.data.find(
                                (cell) => cell.id === "otherAmount"
                              );
                              if (othAmtCell) {
                                othAmtCell.value = formatAmount(totalAmount);
                                othAmtCell.otherAmountEntries = finalEntries;
                              }

                              // Update Less Weight (always show with 2 decimal places)
                              const lsWtCell = row.data.find(
                                (cell) => cell.id === "lsWt"
                              );
                              if (lsWtCell) {
                                lsWtCell.value =
                                  formatLessWeight(totalLessWeight);
                              }

                              return newData;
                            });

                            // Close modal
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              isOpen: false,
                            }));
                          }
                        }
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        // When focus reaches the amount field, add a new row in the background
                        setTimeout(() => {
                          setOtherAmountModal((prev) => {
                            // Only add new row if current row has any data
                            if (
                              prev.currentEntry.chargeType ||
                              prev.currentEntry.pcs ||
                              prev.currentEntry.ct ||
                              prev.currentEntry.wt ||
                              prev.currentEntry.conPercentage ||
                              prev.currentEntry.rateOn ||
                              prev.currentEntry.rate
                            ) {
                              return {
                                ...prev,
                                entries: [...prev.entries],
                                currentEntry: { ...prev.currentEntry },
                              };
                            }
                            return prev;
                          });
                        }, 0);
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        // Move to next row on blur if amount is valid
                        const value = otherAmountModal.currentEntry.amount;
                        if (value && !isNaN(value) && Number(value) > 0) {
                          // Add current entry and move to next row
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: [
                              ...prev.entries,
                              { ...prev.currentEntry },
                            ],
                            currentEntry: {
                              chargeType: "",
                              pcs: "",
                              ct: "",
                              wt: "",
                              conPercentage: "",
                              rateOn: "",
                              rate: "",
                              amount: "",
                            },
                          }));
                          // Focus on first cell of next row
                          setTimeout(() => {
                            const firstInput = document.querySelector(
                              ".modal-body tbody tr:last-child td:nth-child(2) input"
                            );
                            if (firstInput) {
                              firstInput.focus();
                            }
                          }, 0);
                        }
                      }}
                      className={`form-control border-0 rounded-0 ${buttonClass}`}
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>{/* Remove the total row from table */}</tfoot>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                LWt:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                {formatLessWeight(
                  [
                    ...otherAmountModal.entries,
                    otherAmountModal.currentEntry,
                  ].reduce((sum, entry) => {
                    const lessWeight = calculateLessWeight(entry);
                    return sum + (isNaN(lessWeight) ? 0 : lessWeight);
                  }, 0)
                )}
              </span>
            </div>
            <div
              className="border-end h-100 mx-2"
              style={{ width: "1px", backgroundColor: "#dee2e6" }}
            ></div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                Approx Total:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                ₹{" "}
                {formatAmount(
                  [
                    ...otherAmountModal.entries,
                    otherAmountModal.currentEntry,
                  ].reduce((sum, entry) => sum + Number(entry.amount || 0), 0)
                )}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveOtherAmountEntries}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase())
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };

  // Add calculation functions before the Table component
  const calculateTotalCt = (entry) => {
    const ct = parseFloat(entry.ct);

    if (isNaN(ct)) return 0;
    return ct;
  };

  const calculateTotalWt = (entry) => {
    const pcs = parseFloat(entry.pcs);
    const wt = parseFloat(entry.wt);

    if (isNaN(pcs) || isNaN(wt)) return 0;
    return pcs * wt;
  };

  const calculateLessWeight = (entry) => {
    // Calculate total weight first
    const totalWt = calculateTotalWt(entry);
    if (!totalWt || isNaN(totalWt)) return 0;

    // Get concentration percentage
    const conPercentage = parseFloat(entry.conPercentage);

    // If there's a valid concentration percentage, calculate less weight based on that
    if (!isNaN(conPercentage) && conPercentage > 0) {
      return totalWt * (conPercentage / 100);
    }

    // If no concentration percentage, return the total weight
    return totalWt;
  };

  const calculateAmount = (entry) => {
    if (!entry.rate || isNaN(entry.rate)) return 0;

    const rate = parseFloat(entry.rate);
    const rateOn = entry.rateOn?.toLowerCase() || "";
    let amount = 0;

    switch (rateOn) {
      case "fixed":
        amount = rate;
        break;
      case "pcs":
        amount =
          entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
        break;
      case "ct":
        const totalCt = calculateTotalCt(entry);
        amount = totalCt ? rate * totalCt : 0;
        break;
      case "wt":
        const totalWt = calculateTotalWt(entry);
        amount = totalWt ? rate * totalWt : 0;
        break;
      default:
        amount = 0;
    }

    // Return the formatted amount
    return amount;
  };

  const calculateTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const lbrAmt = parseFloat(
        row.data.find((cell) => cell.id === "lbrAmount")?.value || 0
      );
      const othAmt = parseFloat(
        row.data.find((cell) => cell.id === "otherAmount")?.value || 0
      );
      return sum + (isNaN(lbrAmt) ? 0 : lbrAmt) + (isNaN(othAmt) ? 0 : othAmt);
    }, 0);
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Add this function to calculate Labor Amount
  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, metalType) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      const masterRate =
        metalType === "Gold" ? masterGoldRate : masterSilverRate;
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        masterRate,
        metalType
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  const calculateAmountBeforeRoundOff = () => calculateTotal();

  const getAutoPaymentRoundOff = () =>
    autoRoundOff(calculateAmountBeforeRoundOff()).roundOff;

  const getEffectiveRoundOff = () => {
    if (isManualRoundOff) {
      return parseFloat(roundOffAmount) || 0;
    }
    return getAutoPaymentRoundOff();
  };

  const getDisplayRoundOff = () => {
    if (isManualRoundOff) {
      return roundOffAmount;
    }
    return getAutoPaymentRoundOff().toFixed(2);
  };

  // Calculate Approx Total (Approx Final Amount + round-off adjustment)
  const calculateGrandTotal = () => {
    const amountBeforeRoundOff = calculateAmountBeforeRoundOff();
    const roundOff = getEffectiveRoundOff();
    return (amountBeforeRoundOff + roundOff).toFixed(2);
  };

  // Define product and setProductGroups using useState

  const [design, setDesign] = useState(""); // Initialize design state
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lType, setLType] = useState(""); // Initialize lType state
  const [lbrPercentage, setLbrPercentage] = useState(0); // Initialize lbrPercentage state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [mrp, setMrp] = useState(0); // Initialize mrp state
  const [image, setImage] = useState(null); // Initialize image state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [productGroup, setProductGroup] = useState(""); // Initialize productGroup state (assuming it's a string)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [roundOffAmount, setRoundOffAmount] = useState("");
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [note, setNote] = useState("");

  // Add resetForm function before handleSubmit
  const resetForm = () => {
    // Reset bill type and date
    setBillDate(new Date().toISOString().split("T")[0]);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset payment rows
    setPaymentRows([
      {
        id: 1,
        pay_mode: "cash", // Pre-selected cash
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 2,
        pay_mode: "cheque", // Pre-selected cheque
        type: "",
        remarks: "",
        amount: "",
      },
      {
        id: 3,
        pay_mode: "bank_transfer", // Pre-selected bank transfer
        type: "",
        remarks: "",
        amount: "",
      },
    ]);

    // Reset other fields
    setRoundOffAmount("");
    setIsManualRoundOff(false);
    setNote("");
    setFollowUpDate("");
    setExpDate(new Date().toISOString().split("T")[0]); // Reset Exp Delivery to today
    setExpDueDate(""); // Reset global Exp Date
    setLType(""); // Reset LBR Type
    setPayableRows([
      {
        id: 1,
        pay_mode: "cash",
        type: "",
        remarks: "",
        amount: "",
      },
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const itemErrors = collectLineItemFieldErrors(
        tableData,
        REPAIRING_ORDER_LINE_ERROR_OPTIONS,
      );
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      if (!customerMobile && !customerMobileSearchText) {
        toast.error("Please select a customer");
        return;
      }

      const emailError = getEmailValidationError(customerDetails?.email);
      if (emailError) {
        toast.error(emailError);
        return;
      }

      const mobileToSearch = customerMobileSearchText || customerMobile;
      const selectedCustomerLookup =
        userss.find(
          (c) =>
            selectedCustomerId != null &&
            !String(selectedCustomerId).startsWith("TEMP_") &&
            String(c.id) === String(selectedCustomerId),
        ) ||
        userss.find(
          (c) => String(c.mobile).trim() === String(mobileToSearch).trim(),
        ) ||
        null;
      const selectedCustomer = selectedCustomerLookup || null;

      const isNewCustomer = isEditing
        ? false
        : !selectedCustomer ||
          (selectedCustomer.id &&
            String(selectedCustomer.id).toString().startsWith("TEMP_"));

      if (isNewCustomer && !isValidMobileNumber(mobileToSearch)) {
        setCustomerMobileInvalid(true);
        toast.error("Enter valid number");
        return;
      }
      setCustomerMobileInvalid(false);

      let newUserData = [];
      let userIdToSend = isEditing
        ? selectedCustomerId
        : selectedCustomer
          ? selectedCustomer.id
          : null;
      if (isNewCustomer) {
        userIdToSend = null;
      }
      if (!isEditing && (isNewCustomer || customerSaveSuccess)) {
        const sc = selectedCustomer || {};
        const defaultState = "Gujarat";
        const defaultCity = "Ahmedabad";
        newUserData = [
          {
            name:
              sc.name ||
              sc.fullName ||
              customerNameSearchText ||
              customerName ||
              "",
            mobile:
              sc.mobile || customerMobile || customerMobileSearchText || "",
            email: sc.email || customerDetails.email || "",
            city: sc.city || customerCity || defaultCity,
            address: sc.address || customerAddress || "",
            aadhar_no: sc.aadharNo || customerDetails.aadharNo || "",
            pan_no: sc.panNo || customerDetails.panNo || "",
            birth_date:
              sc.birthDate || customerDetails.birthDate || "0000-00-00",
            marital_status:
              sc.maritalStatus || customerDetails.maritalStatus || "",
            anniversary_date:
              sc.anniversaryDate ||
              customerDetails.anniversaryDate ||
              "0000-00-00",
            balance: sc.balance || customerDetails.balance || "",
            sale_balance: sc.saleBalance || customerDetails.saleBalance || "",
            company_name: sc.companyName || customerDetails.companyName || "",
            company_address:
              sc.companyAddress || customerDetails.companyAddress || "",
            GST_number: sc.gstNumber || customerDetails.gstNumber || "",
            reference: sc.reference || customerDetails.reference || "",
            state: sc.state || customerDetails.state || defaultState,
            area: sc.area || customerDetails.area || "",
            status: "1",
          },
        ];
      }

      // Support both legacy encrypted userData and ERP OTP auth user-details.
      const legacyUser =
        (typeof getUserData === "function" ? getUserData() : null) || {};
      const erpUser = getErpAuthState?.()?.user || {};
      const by_user =
        legacyUser?.id ??
        legacyUser?.ua_id ??
        erpUser?.id ??
        erpUser?.ua_id ??
        erpUser?.user_id;
      if (!by_user) {
        toast.error("User not logged in");
        return;
      }

      // Format expected delivery date
      const formattedExpDelivery = expDate
        ? new Date(expDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      if (hasItemErrors) {
        return;
      }

      // Repairing Sales → POST /api/sales type create (plain JSON + idempotency_key)
      if (isRepairingSale) {
        const grandTotal = parseFloat(calculateGrandTotal()) || 0;
        const dateStr = new Date().toISOString().split("T")[0];

        let customerIdNum = userIdToSend;
        if (isNewCustomer && newUserData.length > 0) {
          const u = newUserData[0];
          const custRes = await createCustomerUser({
            name: u.name,
            mobile: String(u.mobile || "").trim(),
            email: u.email || undefined,
            city: u.city || undefined,
            address: u.address || undefined,
            state: u.state || undefined,
            area: u.area || undefined,
            aadhar_no: u.aadhar_no || undefined,
            pan_no: u.pan_no || undefined,
            birth_date: u.birth_date || undefined,
            marital_status: u.marital_status || undefined,
            anniversary_date: u.anniversary_date || undefined,
            company_name: u.company_name || undefined,
            company_address: u.company_address || undefined,
            gst_number: u.GST_number || undefined,
            reference: u.reference || undefined,
            balance: Number(u.balance) || 0,
          });
          if (!custRes?.success) {
            toast.error(custRes?.message || "Failed to create customer");
            return;
          }
          customerIdNum =
            custRes.data?.id ??
            custRes.data?.customer_id ??
            custRes.customer_id ??
            null;
        }
        if (
          customerIdNum == null ||
          !Number.isFinite(Number(customerIdNum)) ||
          Number(customerIdNum) <= 0
        ) {
          toast.error("Invalid customer");
          return;
        }

        const apiLineItems = [];
        for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
          const row = tableData[rowIndex];
          const rowData = row.data.reduce((acc, cell) => {
            acc[cell.id] = cell.value;
            return acc;
          }, {});
          if (!rowData.productGroup || !rowData.currentWeight) {
            continue;
          }
          const tag = String(rowData.design || "").trim();
          let productId = null;
          if (tag) {
            try {
              const tagRes = await showProducts({ tag_no: tag });
              const p = tagRes?.data?.product ?? tagRes?.data;
              if (isErpApiSuccess(tagRes) && p?.id != null) {
                productId = Number(p.id);
              }
            } catch (_) {
              /* ignore */
            }
          }
          if (!productId) {
            toast.error(
              `Row ${rowIndex + 1}: enter Design / tag_no that matches stock so product_id can be resolved.`,
            );
            return;
          }
          const gwt =
            parseFloat(
              rowData.grWt || rowData.currentWeight || rowData.gwt || 0,
            ) || 0;
          const nwtRaw =
            parseFloat(rowData.netWt || rowData.nwt || 0) || 0;
          const nwt = nwtRaw > 0 ? nwtRaw : gwt;
          const lwt =
            parseFloat(
              rowData.lsWt || rowData.lessWt || rowData.lwt || 0,
            ) || 0;
          apiLineItems.push({
            product_id: productId,
            tag_no: tag,
            pcs: Number(rowData.pcs) || 0,
            gwt,
            lwt,
            nwt,
            item_rate_on: mapItemRateOn(rowData.itRateOn || "NWt"),
            item_rate: parseFloat(rowData.itRate || rowData.itrate || 0) || 0,
            item_amount:
              parseFloat(rowData.itAmt || rowData.itamt || 0) || 0,
            lbr_type: mapLbrTypeForApi(rowData.lType),
            lbr_amt: parseFloat(rowData.lbrAmount || 0) || 0,
            total_amount: parseFloat(rowData.totalAmount || 0) || 0,
          });
        }

        if (apiLineItems.length === 0) {
          toast.error("No valid product lines for sale");
          return;
        }

        if (!repairingSaleIdempotencyKeyRef.current) {
          repairingSaleIdempotencyKeyRef.current = createSalesIdempotencyKey();
        }

        const salePaymentPersistKeys = [];
        const paymentsPayload = buildStandardPaymentLines(paymentRows, {
          isUpdate: false,
          paymentTypes,
          payModeLabels: PAYMENT_METHOD_LABELS,
          createKey: createSalesIdempotencyKey,
          persistKeys: salePaymentPersistKeys,
        });

        const createPayload = {
          customer_id: Number(customerIdNum),
          date: dateStr,
          bill_type_id: selectedBillTypeId ? Number(selectedBillTypeId) : null,
          bill_type_name: selectedBillTypeName || "",
          discount: 0,
          tax_amount: 0,
          round_off: getEffectiveRoundOff(),
          grand_total: grandTotal,
          notes: note || "",
          idempotency_key: repairingSaleIdempotencyKeyRef.current,
          items: apiLineItems,
          payments: paymentsPayload,
        };
        if (
          location.state?.repairingOrderId != null &&
          location.state?.repairingOrderId !== ""
        ) {
          createPayload.repairing_id = location.state.repairingOrderId;
        }
        if (
          queueDetailsId !== undefined &&
          queueDetailsId !== null &&
          queueDetailsId !== ""
        ) {
          createPayload.queue_details_id = queueDetailsId;
          if (customOrderData?.by_user != null) {
            createPayload.queued_by_user = customOrderData.by_user;
          }
        }

        const saleRes = await createSale(createPayload);

        if (isSalesApiSuccess(saleRes)) {
          repairingSaleIdempotencyKeyRef.current = null;
          toast.success("Sales submitted successfully");
          setShowSuccessModal(true);
          setTimeout(() => {
            setShowSuccessModal(false);
            resetForm();
          }, 2000);
        } else {
          toast.error(saleRes?.message || "Failed to create sales");
        }
        return;
      }

      // Process repairing order entries with FormData support
      const repairingOrderEntries = [];
      const formData = new FormData();

      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          return acc;
        }, {});

        const productGroupCell = tableData[rowIndex].data.find(
          (c) => c.id === "productGroup",
        );
        const hasProductGroup =
          (productGroupCell?.value &&
            String(productGroupCell.value).trim() !== "") ||
          (productGroupCell?.productGroupId !== null &&
            productGroupCell?.productGroupId !== undefined &&
            String(productGroupCell.productGroupId) !== "0" &&
            String(productGroupCell.productGroupId).trim() !== "");

        // Skip rows with empty required fields.
        // In edit mode, productGroup label can be blank while productGroupId exists.
        // Allow pcs to be empty (will default to 0 in payload)
        if (!hasProductGroup || !rowData.currentWeight) {
          continue;
        }

        // Handle image upload for this row - get the image file
        const imageCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "image"
        );
        const imageFile = imageCell ? imageCell.imageFile : null;

        // Create repairing order entry in the format the API expects
        const originalEntryId = tableData[rowIndex].originalEntryId || null;
        const entryPayload = {
          product_group: productGroupCell?.productGroupId || 0,
          pcs: Number(rowData.pcs) || 0,
          current_weight: Number(rowData.currentWeight) || 0,
          exp_weight: Number(rowData.expWeight) || 0,
          issue_description: rowData.issueDescription || "",
          lbr_type: rowData.lType || "",
          lbr_percentage: Number(rowData.lbrPercentage) || 0,
          lbr_rate: Number(rowData.lbrRate) || 0,
          lbr_amount: Number(rowData.lbrAmount) || 0,
          other_amount: Number(rowData.otherAmount) || 0,
          total_amount: parseFloat(rowData.totalAmount) || 0,
        };
        if (isEditing && originalEntryId) {
          entryPayload.id = originalEntryId;
        }
        repairingOrderEntries.push(entryPayload);

        // Add image file to FormData if present
        if (imageFile) {
          // When editing: backend expects image_{entry_id} for existing rows
          // When inserting new rows during edit: fallback key image_new_{rowIndex}
          const key =
            isEditing && originalEntryId
              ? `image_${originalEntryId}`
              : isEditing
                ? `image_new_${rowIndex}`
                : `image_${rowIndex}`;
          formData.append(key, imageFile, imageFile.name);
          // console.log(`Added image file with key ${key}:`, imageFile.name);
        }
      }

      // Check if we have any valid entries
      if (repairingOrderEntries.length === 0) {
        for (let i = 0; i < tableData.length; i++) {
          const rowData = tableData[i].data.reduce((acc, cell) => {
            acc[cell.id] = cell.value;
            return acc;
          }, {});
        }
        toast.error(
          "No valid entries to submit. Please complete at least one row."
        );
        return;
      }

      // Process payments
      const paymentPersistKeys = [];
      const repairingOrderPayments = buildStandardPaymentLines(paymentRows, {
        isUpdate: isEditing,
        paymentTypes,
        payModeLabels: PAYMENT_METHOD_LABELS,
        createKey: createRepairingOrderIdempotencyKey,
        persistKeys: paymentPersistKeys,
      });

      if (paymentPersistKeys.length > 0) {
        setPaymentRows((prev) => {
          const next = [...prev];
          paymentPersistKeys.forEach(({ rowId, key }) => {
            const idx = next.findIndex((r) => r.id === rowId);
            if (idx >= 0) {
              next[idx] = { ...next[idx], paymentIdempotencyKey: key };
            }
          });
          return next;
        });
      }

      const final_amount =
        parseFloat(String(calculateGrandTotal()).replace(/[^\d.-]/g, "")) || 0;

      // Ensure queue entry has not already been processed elsewhere
      if (queueDetailsId) {
        try {
          const queueEntry =
            findOrderQueueInResponse(
              await showOrderQueue({ id: queueDetailsId }),
              queueDetailsId,
            ) ||
            findOrderQueueInResponse(
              await listOrderQueue({
                search: String(queueDetailsId),
                page: 1,
                limit: 20,
              }),
              queueDetailsId,
            );
          if (
            queueEntry &&
            (!isZeroOrNullValue(queueEntry.sale_id) ||
              !isZeroOrNullValue(queueEntry.custom_order_id) ||
              !isZeroOrNullValue(queueEntry.repairing_id))
          ) {
            toast.error("This order was already processed by another user");
            navigate("/orderqueue");
            return;
          }
        } catch (verifyErr) {
          console.warn("Queue verification failed", verifyErr);
        }
      }

      // Create the repairing order data structure
      const repairingOrderData = {
        repairing_order: {
          // by_user: by_user,
          customer_id: selectedCustomerId,
          bill_type_id: selectedBillTypeId ? Number(selectedBillTypeId) : null,
          bill_type_name: selectedBillTypeName || "",
          exp_delivery: formattedExpDelivery,
          final_amount: final_amount,
          note: note || "",
          ...(followUpDate
            ? {
              followup_date: new Date(followUpDate)
                .toISOString()
                .split("T")[0],
            }
            : isEditing
              ? { followup_date: null }
              : {}),
          entries: repairingOrderEntries,
          payments: repairingOrderPayments,
          ...(isEditing && { id: location.state?.repairingOrderId }),
          queue_details_id:
            queueDetailsId !== undefined &&
              queueDetailsId !== null &&
              queueDetailsId !== ""
              ? queueDetailsId
              : null,
          queued_by_user:
            customOrderData?.by_user !== undefined &&
              customOrderData?.by_user !== null &&
              customOrderData?.by_user !== ""
              ? customOrderData.by_user
              : null,
        },
      };

      // Add the payload data to FormData
      formData.append(
        "repairing_order",
        JSON.stringify(repairingOrderData.repairing_order)
      );
      if (isEditing && location.state?.repairingOrderId != null) {
        // Some update handlers validate `id` at the top-level multipart fields.
        formData.append("id", String(location.state.repairingOrderId));
      }
      formData.append("new_user", JSON.stringify(newUserData));

      const hasPaymentRows = repairingOrderPayments.length > 0;
      if (isEditing) {
        // For update: idempotency_key is required only when payment rows exist.
        if (hasPaymentRows) {
          if (!repairingOrderSubmitIdempotencyKeyRef.current) {
            repairingOrderSubmitIdempotencyKeyRef.current =
              createRepairingOrderIdempotencyKey();
          }
          formData.append(
            "idempotency_key",
            repairingOrderSubmitIdempotencyKeyRef.current,
          );
        } else {
          repairingOrderSubmitIdempotencyKeyRef.current = null;
        }
      } else {
        if (!repairingOrderSubmitIdempotencyKeyRef.current) {
          repairingOrderSubmitIdempotencyKeyRef.current =
            createRepairingOrderIdempotencyKey();
        }
        formData.append(
          "idempotency_key",
          repairingOrderSubmitIdempotencyKeyRef.current,
        );
      }

      const response = isEditing
        ? await updateRepairingOrderMultipart(formData)
        : await createRepairingOrderMultipart(formData);

      if (isErpApiSuccess(response)) {
        repairingOrderSubmitIdempotencyKeyRef.current = null;
        toast.success(
          isEditing
            ? "Repairing order updated successfully"
            : "Repairing order created successfully",
        );
        leaveEdit();
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          resetForm();
        }, 2000);
      } else {
        toast.error(
          response?.message ||
          (isEditing
            ? "Failed to update repairing order"
            : "Failed to create repairing order"),
        );
      }
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit the form."
      );
    }
  };

  // New function to handle labor calculations
  const handleLaborCalculations = (tableData, rowIndex) => {
    const lTypeCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lType"
    );
    const lbrPercentageCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrPercentage"
    );
    const lbrRateCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrRate"
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmount"
    );
    const productGroupCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup"
    );

    const lType = lTypeCell?.value || "";
    const lbrPercentage = parseFloat(lbrPercentageCell?.value) || 0;
    const lbrRate = parseFloat(lbrRateCell?.value) || 0;
    const selectedProductGroup = getProductGroupOptionsList().find(
      (pg) => pg.label === productGroupCell?.value
    );
    const metalType = selectedProductGroup?.metal || "Gold"; // Default to Gold if not found
    const masterRate = metalType === "Gold" ? masterGoldRate : masterSilverRate;
    const perGramRate = calculatePerGramRate(masterRate, metalType);

    // If the labor type is Fixed, clear the percentage and rate fields
    if (lType === "Fixed") {
      if (lbrPercentageCell) lbrPercentageCell.value = "";
      if (lbrRateCell) lbrRateCell.value = "";
      return; // No need to calculate anything for Fixed type
    }

    // Determine the base weight based on labor type
    let baseWeight = 0;
    if (lType === "Exp Wt") {
      const expWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "expWeight"
      );
      baseWeight = parseFloat(expWtCell?.value) || 0;
    } else if (lType === "Pcs") {
      const pcsCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "pcs"
      );
      baseWeight = parseFloat(pcsCell?.value) || 0;
    }

    // Calculate labor amount based on which field was last changed
    let lbrAmt = 0;
    const lastChangedField = tableData[rowIndex].lastChangedLaborField;

    if (lastChangedField === "lbrPercentage" && lbrPercentage > 0) {
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        masterRate,
        metalType
      );
      // Update labor rate based on percentage
      if (lbrRateCell) {
        lbrRateCell.value = ((lbrPercentage / 100) * perGramRate).toFixed(2);
      }
    } else if (lastChangedField === "lbrRate" && lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
      // Update labor percentage based on rate
      if (lbrPercentageCell) {
        lbrPercentageCell.value = ((lbrRate / perGramRate) * 100).toFixed(2);
      }
    } else if (!lastChangedField) {
      // If no field was changed yet (e.g., when changing lType), prefer percentage if available
      if (lbrPercentage > 0) {
        lbrAmt = calculateLbrAmtWithPercentage(
          baseWeight,
          lbrPercentage,
          masterRate,
          metalType
        );
      } else if (lbrRate > 0) {
        lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
      }
    }

    // Update the labor amount cell
    if (lbrAmtCell) {
      lbrAmtCell.value = Math.round(lbrAmt).toString();
      // Recalculate total amount
      calculateTotalAmount(tableData, rowIndex);
    }
  };

  // Handle customer mobile input change
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered customers based on mobile number
  const getFilteredCustomersByMobile = (searchText) => {
    if (!searchText) return userss;
    const searchLower = searchText.toLowerCase();
    return userss.filter((item) =>
      item.mobile.toLowerCase().includes(searchLower)
    );
  };

  // Function to handle customer mobile selection
  const handleCustomerMobileSelect = (selectedItem) => {
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerMobileDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerName(selectedItem.fullName);
    setCustomerNameSearchText(selectedItem.fullName);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
      area: selectedItem.area || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer mobile dropdown
  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredCustomersByMobile(customerMobileSearchText);

    switch (e.key) {
      case "ArrowDown":
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerMobileSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      case "Tab":
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer mobile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer mobile dropdown
  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;

    const filtered = getFilteredCustomersByMobile(customerMobileSearchText);

    // Reset refs array
    customerMobileItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerMobileInputRef.current
      ? customerMobileInputRef.current.offsetWidth
      : 150;
    const inputRect = customerMobileInputRef.current
      ? customerMobileInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.mobile.toLowerCase() ===
              customerMobileSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerMobileItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerMobileHighlightedIndex ? "active" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerMobileHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerMobileHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerMobileSelect(item)}
              >
                {item.mobile} - {item.fullName || item.name || ""}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching customers</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer name input change
  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    const hasValue = value.trim().length > 0;

    setCustomerNameSearchText(value);
    setCustomerNameDropdownActive(hasValue);
    setCustomerNameHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(hasValue);
  };

  // Get filtered customers based on name
  const getFilteredCustomersByName = (searchText) => {
    if (!searchText) return CUSTOMERS;
    const searchLower = searchText.toLowerCase();
    return CUSTOMERS.filter((item) =>
      item.name.toLowerCase().includes(searchLower)
    );
  };
  // Function to handle customer name selection
  const handleCustomerNameSelect = (selectedItem) => {
    setCustomerName(selectedItem.name);
    setCustomerNameSearchText(selectedItem.name);
    setCustomerNameDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
      area: selectedItem.area || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer name dropdown
  const handleCustomerNameKeyDown = (e) => {
    const filtered = getFilteredCustomersByName(customerNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerNameDropdownActive) {
          handleCustomerNameSelect(filtered[customerNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerNameDropdownActive(false);
        break;
      case "Tab":
        setCustomerNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer name dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerNameDropdownActive(false);
      }
      if (
        karigarNameDropdownRef.current &&
        !karigarNameDropdownRef.current.contains(event.target) &&
        karigarNameInputRef.current &&
        !karigarNameInputRef.current.contains(event.target)
      ) {
        setKarigarNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer name dropdown
  const renderCustomerNameDropdown = () => {
    if (!customerNameDropdownActive) return null;

    const filtered = getFilteredCustomersByName(customerNameSearchText);

    // Reset refs array
    customerNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerNameInputRef.current
      ? customerNameInputRef.current.offsetWidth
      : 150;
    const inputRect = customerNameInputRef.current
      ? customerNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.name.toLowerCase() === customerNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerNameHighlightedIndex ? "active" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerNameHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerNameSelect(item)}
              >
                {item.name} - {item.mobile}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching customers</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer address change
  const handleCustomerAddressChange = (e) => {
    setCustomerAddress(e.target.value);
  };

  // Handle customer city change
  const handleCustomerCityChange = (e) => {
    setCustomerCity(e.target.value);
  };

  // Function to save a new customer
  const handleSaveCustomer = async () => {
    if (!customerMobileSearchText || !customerNameSearchText) {
      toast.error("Mobile number and name are required");
      return;
    }

    try {
      const response = await createCustomerUser({
        mobile: customerMobileSearchText.trim(),
        name: customerNameSearchText.trim(),
        address: customerAddress || undefined,
        city: customerCity || undefined,
        state: customerDetails.state || undefined,
      });

      if (isErpApiSuccess(response)) {
        toast.success("Customer created successfully");

        const created =
          response?.data?.customer ??
          response?.data?.user ??
          response?.data;
        const mapped = mapShowCustomerToUi(created);
        const fallbackSearch = customerMobileSearchText.trim();

        setCustomerMobile("");
        setCustomerMobileSearchText("");
        setCustomerName("");
        setCustomerNameSearchText("");
        setCustomerAddress("");
        setCustomerCity("");
        setCustomerDetails((prev) => ({ ...prev, state: "" }));

        setIsNewCustomer(false);
        if (mapped) {
          mergeUserss([mapped]);
          applyCustomerToForm(mapped);
        } else if (fallbackSearch.length >= 3) {
          void fetchUserss(fallbackSearch);
        }
      } else {
        toast.error(response?.message || "Failed to save customer");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Error saving customer");
    }
  };

  const [paymentTypes, setPaymentTypes] = useState([]);

  // Initialize payment rows state with three common payment methods
  const [paymentRows, setPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 2,
      pay_mode: "cheque",
      type: "",
      remarks: "",
      amount: "",
    },
    {
      id: 3,
      pay_mode: "bank_transfer",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  useEffect(() => {
    if (bankAccounts.length > 0 && paymentRows.length > 0) {
      let hasChanges = false;
      const updatedRows = paymentRows.map((row) => {
        if (row.bank_register_id != null) {
          const accountByRegister = bankAccounts.find(
            (b) =>
              String(b.bankRegisterId ?? "") === String(row.bank_register_id)
          );
          if (
            accountByRegister &&
            String(row.type || "") !== String(accountByRegister.id)
          ) {
            hasChanges = true;
            return { ...row, type: accountByRegister.id };
          }
        }
        if (
          !row.type &&
          ["cash", "wallet", "kasar", "old_gold", "old_silver"].includes(
            row.pay_mode
          )
        ) {
          const findAccount = (keyword) =>
            bankAccounts.find(
              (b) =>
                (b.accountType &&
                  (b.accountType.toLowerCase() === keyword ||
                    b.accountType.toLowerCase() ===
                    keyword.replace("_", " "))) ||
                (b.bankName &&
                  b.bankName.toLowerCase().includes(keyword.replace("_", " ")))
            );
          const account = findAccount(row.pay_mode);
          if (account) {
            hasChanges = true;
            return { ...row, type: account.id };
          }
        }
        return row;
      });
      if (hasChanges) {
        setPaymentRows(updatedRows);
      }
    }
  }, [bankAccounts]);

  // Payment completion tracking state
  const [followUpDate, setFollowUpDate] = useState("");

  // Calculate total amount from payment rows
  const calculatePaymentTotal = () => {
    return paymentRows
      .reduce((sum, row) => {
        const amount = parseFloat(row.amount) || 0;
        return sum + amount;
      }, 0)
      .toFixed(2);
  };

  // Calculate remaining amount (Grand Total - Payment Total)
  const calculateRemainingAmount = () => {
    // Get grand total without the currency symbol
    const grandTotal = parseFloat(
      calculateGrandTotal().replace(/[^\d.-]/g, "")
    );
    const paymentTotal = parseFloat(calculatePaymentTotal());

    const remaining = grandTotal - paymentTotal;
    // Return 0 if negative or very small number (accounting for floating point errors)
    return Math.max(0, remaining.toFixed(2));
  };

  const calculatePayableAmount = () => {
    const totalPaid = parseFloat(calculatePaymentTotal()) || 0;
    const grandTotal =
      parseFloat(String(calculateGrandTotal()).replace(/[^\d.-]/g, "")) || 0;
    if (totalPaid > grandTotal) {
      return (totalPaid - grandTotal).toFixed(2);
    }
    return "0.00";
  };

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle round-off amount change
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(
      e,
      (value) => {
        setIsManualRoundOff(true);
        setRoundOffAmount(value);
      },
      {
        allowNegative: true,
        simple: true,
      },
    );
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  // Add expDueDate state
  const [expDueDate, setExpDueDate] = useState("");

  // Handler for Exp Due Date
  const handleExpDueDateChange = (e) => {
    setExpDueDate(e.target.value);
  };

  // Handle karigar name input change
  const handleKarigarNameChange = (e) => {
    const value = e.target.value;
    setKarigarNameSearchText(value);
    setKarigarNameDropdownActive(true);
    setKarigarNameHighlightedIndex(0);
    setSelectedKarigarId(null);
  };

  // Get filtered karigars based on name
  const getFilteredKarigarsByName = (searchText) => {
    if (!searchText || searchText.length < 3) return [];
    // Only show active karigars
    const activeKarigars = getActiveItems(karigars, {
      statusField: "status",
      activeValue: "1",
    });
    return activeKarigars.filter((karigar) => {
      const displayName = karigar.companyName
        ? `${karigar.fullName} (${karigar.companyName})`
        : karigar.fullName;
      return (
        karigar.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
        karigar.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
        karigar.shortCode.toLowerCase().includes(searchText.toLowerCase()) ||
        displayName.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  };

  // Function to handle karigar name selection
  const handleKarigarNameSelect = (selectedItem) => {
    const displayName = selectedItem.companyName
      ? `${selectedItem.fullName} (${selectedItem.companyName})`
      : selectedItem.fullName;
    setKarigarName(displayName);
    setKarigarNameSearchText(displayName);
    setKarigarNameDropdownActive(false);
    setSelectedKarigarId(selectedItem.id);
  };

  // Handle key navigation in karigar name dropdown
  const handleKarigarNameKeyDown = (e) => {
    const filtered = getFilteredKarigarsByName(karigarNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setKarigarNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (karigarNameItemsRef.current[newIndex]) {
            karigarNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setKarigarNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (karigarNameItemsRef.current[newIndex]) {
            karigarNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && karigarNameDropdownActive) {
          handleKarigarNameSelect(filtered[karigarNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setKarigarNameDropdownActive(false);
        break;
      case "Tab":
        setKarigarNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Render karigar name dropdown
  const renderKarigarNameDropdown = () => {
    if (!karigarNameDropdownActive) return null;

    const filtered = getFilteredKarigarsByName(karigarNameSearchText);

    // Reset refs array
    karigarNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = karigarNameInputRef.current
      ? karigarNameInputRef.current.offsetWidth
      : 150;
    const inputRect = karigarNameInputRef.current
      ? karigarNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    return ReactDOM.createPortal(
      <div
        ref={karigarNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.fullName.toLowerCase() ===
              karigarNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (karigarNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === karigarNameHighlightedIndex ? "active" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === karigarNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === karigarNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setKarigarNameHighlightedIndex(idx)}
                onMouseDown={() => handleKarigarNameSelect(item)}
              >
                {item.fullName} {item.companyName && `(${item.companyName})`}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching karigars</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // --- Add state for Karigar Add Modal ---
  const [showAddKarigarModal, setShowAddKarigarModal] = useState(false);
  const [karigarFullName, setKarigarFullName] = useState("");
  const [karigarCompanyName, setKarigarCompanyName] = useState("");
  const [karigarShortCode, setKarigarShortCode] = useState("");
  const [karigarContactNumber, setKarigarContactNumber] = useState("");
  const [karigarEmail, setKarigarEmail] = useState("");
  const [karigarAddress, setKarigarAddress] = useState("");
  const [karigarCity, setKarigarCity] = useState("");
  const [karigarGstNumber, setKarigarGstNumber] = useState("");
  const [karigarStatus, setKarigarStatus] = useState("active");
  const [karigarSaving, setKarigarSaving] = useState(false);

  // Generate short code from company name
  useEffect(() => {
    setKarigarShortCode(
      karigarCompanyName
        .split(" ")
        .filter((word) => word && !word.includes("-"))
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
    );
  }, [karigarCompanyName]);

  // --- Add Karigar Modal submit handler ---
  const handleAddKarigarSubmit = async (e) => {
    e.preventDefault();

    const karigarMobileError = getMobileValidationError(karigarContactNumber, {
      required: true,
    });
    if (karigarMobileError) {
      toast.error(karigarMobileError);
      return;
    }

    const karigarEmailError = getEmailValidationError(karigarEmail);
    if (karigarEmailError) {
      toast.error(karigarEmailError);
      return;
    }

    setKarigarSaving(true);
    const requestData = {
      name: karigarFullName,
      company_name: karigarCompanyName,
      short_code: karigarShortCode,
      contact_number: karigarContactNumber,
      email: karigarEmail,
      address: karigarAddress,
      city: karigarCity,
      gst_number: karigarGstNumber,
      primary_supplier: 0,
    };
    // console.log("Sending Karigar request data:", requestData);
    try {
      const response = await createSupplier(requestData);
      if (response?.success) {
        toast.success("Karigar added successfully");
        setShowAddKarigarModal(false);
        // Reset form fields...

        setKarigarFullName("");
        setKarigarCompanyName("");
        setKarigarShortCode("");
        setKarigarContactNumber("");
        setKarigarEmail("");
        setKarigarAddress("");
        setKarigarCity("");
        setKarigarGstNumber("");
        setKarigarStatus("active");
        const created = response?.data?.supplier ?? response?.data;
        if (created?.id) {
          const mapped = {
            id: created.id,
            fullName: created.name || karigarFullName || "",
            companyName: created.company_name || karigarCompanyName || "",
            shortCode: created.short_code || karigarShortCode || "",
            contactNumber: created.contact_number || karigarContactNumber || "",
            email: created.email || "",
            address: created.address || "",
            city: created.city || "",
            gstNumber: created.gst_number || created.GST_number || "",
            status: created.status || "1",
            isPrimary: 0,
          };
          setKarigars((prev) => {
            const byId = new Map(
              (Array.isArray(prev) ? prev : []).map((k) => [String(k.id), k]),
            );
            byId.set(String(mapped.id), mapped);
            return Array.from(byId.values());
          });
        } else if (karigarNameSearchText.trim().length >= 3) {
          void fetchKarigars(karigarNameSearchText.trim());
        }
      } else {
        toast.error(response?.message || "Failed to add karigar");
      }
    } catch (error) {
      console.error("Karigar API error:", error, error.response?.data);
      toast.error(error.response?.data?.message || "Failed to add karigar");
    } finally {
      setKarigarSaving(false);
    }
  };

  return (
    <div className={isEmbedded ? cardClass : `body-wrapper ${cardClass}`}>
      <div className={isEmbedded ? "" : "container-fluid"}>
        <div className={`card shadow-sm ${cardClass} ${isEmbedded ? "border-0 mb-0" : ""}`}>
          <div
            className={`border-bottom ${isEmbedded ? "px-3 py-2" : "px-4 py-3"}`}
          >
            <div className="d-flex align-items-center flex-wrap gap-3 w-100">
              <h4 className={`card-title mb-0 ${isEmbedded ? "fs-5" : ""}`}>
                {pageTitle ||
                  (isViewOnly
                    ? "View Repair Order"
                    : isRepairingSale
                      ? "Repairing Sales"
                      : "Add Repairing Order")}
              </h4>
              <div className="d-flex align-items-center flex-shrink-0">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 text-nowrap"
                    style={{
                      minWidth: "74px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Bill Type:
                  </label>
                  <div style={billTypeHeaderSelectWrapStyle}>
                    <Select
                      styles={billTypeHeaderSelectStyles}
                      menuPlacement="bottom"
                      isSearchable={false}
                      options={billTypes
                        .filter(
                          (item) =>
                            String(item.id) !== String(selectedBillTypeId || ""),
                        )
                        .map((item) => ({
                          value: String(item.id),
                          label: item.name,
                        }))}
                      value={
                        (() => {
                          const selected =
                            billTypes.find(
                              (item) =>
                                String(item.id) === String(selectedBillTypeId),
                            ) ||
                            billTypes.find(
                              (item) =>
                                item.name.toLowerCase() ===
                                String(selectedBillTypeName || "").toLowerCase(),
                            ) ||
                            billTypes.find(
                              (item) => item.name.toLowerCase() === "gst",
                            ) ||
                            null;
                          return selected
                            ? {
                              value: String(selected.id),
                              label: selected.name,
                            }
                            : selectedBillTypeName
                              ? {
                                value: String(selectedBillTypeId || ""),
                                label: selectedBillTypeName,
                              }
                              : null;
                        })()
                      }
                      onChange={(option) => {
                        const selected = billTypes.find(
                          (item) => String(item.id) === String(option?.value),
                        );
                        applyBillTypeSelection(selected);
                      }}
                    />
                  </div>
                </div>
              </div>
              {/* Date Component */}
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Date:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={billDate}
                  onChange={handleDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>
              <div className="d-flex align-items-center flex-shrink-0">
                <label
                  className="form-label me-2 mb-0 text-nowrap"
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: "600",
                  }}
                >
                  Exp Delivery:
                </label>
                <input
                  type="date"
                  className={`form-control-date form-control-date-compact form-control-sm ${buttonClass}`}
                  value={expDate}
                  onChange={handleExpDateChange}
                  style={{ fontSize: "0.85rem" }}
                />
              </div>

              {(selectedCustomerId || productGroupPoolHeader || isEmbedded) && (
                <div className="d-flex align-items-center flex-nowrap gap-2 ms-auto">
                  {selectedCustomerId && (
                    <>
                      <span
                        className={`badge ${
                          parseFloat(customerDetails.balance) >= 0
                            ? "bg-success-subtle text-success"
                            : "bg-danger-subtle text-danger"
                        }`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Balance: ₹
                        {parseFloat(customerDetails.balance || 0).toFixed(2)}
                      </span>
                      <span
                        className={`badge ${
                          parseFloat(customerDetails.saleBalance) > 0
                            ? "bg-danger-subtle text-danger"
                            : "bg-success-subtle text-success"
                        }`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Pending: ₹
                        {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                      </span>
                    </>
                  )}
                  <ProductGroupPoolBalance
                    balance={productGroupPoolHeader}
                    className=""
                  />
                  {isEmbedded && (
                    <button
                      type="button"
                      className="btn-close flex-shrink-0"
                      aria-label="Close"
                      onClick={() => onClose()}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            <CustomerSelection
              customerMobile={customerMobile}
              setCustomerMobile={setCustomerMobile}
              customerMobileSearchText={customerMobileSearchText}
              setCustomerMobileSearchText={setCustomerMobileSearchText}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerNameSearchText={customerNameSearchText}
              setCustomerNameSearchText={setCustomerNameSearchText}
              customerAddress={customerAddress}
              setCustomerAddress={setCustomerAddress}
              customerDetails={customerDetails}
              setCustomerDetails={setCustomerDetails}
              customerCity={customerCity}
              setCustomerCity={setCustomerCity}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              customerSaveSuccess={customerSaveSuccess}
              setCustomerSaveSuccess={setCustomerSaveSuccess}
              users={userss}
              setUsers={setUserss}
              states={states}
              cities={cities}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              buttonClass={buttonClass}
              savingCustomer={savingCustomer}
              readOnlyCustomerFields={Boolean(location.state?.repairingOrderId)}
              hideAddButton={Boolean(location.state?.repairingOrderId)}
              mobileInvalid={customerMobileInvalid}
              onClearMobileInvalid={() => setCustomerMobileInvalid(false)}
              excludeMobile={
                queueDetailsId
                  ? customerMobileSearchText || customerMobile || null
                  : null
              }
            />
            <div className="row mb-1">
              {/* Date Component */}
              {/* <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold"
                    style={{ width: "80px" }}
                  >
                    Date:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={billDate}
                    onChange={handleDateChange}
                  />
                </div>
              </div> */}

              {/* City and Save Button Container - Adjust city visibility based on isNewCustomer */}
            </div>

            <div
              ref={tableContainerRef}
              className={`table-responsive mt-3 ${buttonClass}`}
              style={{
                maxHeight:
                  tableData.length > 6
                    ? `${40 + 6 * 38 + 38}px`
                    : undefined,
                overflowY: tableData.length > 6 ? "auto" : "visible",
              }}
            >
              {/* No table-rounded/overflow-hidden — breaks sticky header/total */}
              <table
                className="table table-bordered table-hover table-sm mb-0 theme-header-table"
                style={{ width: "auto" }}
              >
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 6 ? "sticky" : undefined,
                          top: tableData.length > 6 ? 0 : undefined,
                          zIndex: tableData.length > 6 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  onMouseEnter={(e) => {
                                    e.target.textContent = "×";
                                    e.target.style.fontWeight = "600";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.textContent = rowIndex + 1;
                                    e.target.style.fontWeight = "normal";
                                  }}
                                >
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              ...lineCellErrorStyle(hasFieldError),
                            }}
                          >
                            {getColumnType(colIndex) === "karigarName" ? (
                              // Karigar dropdown with customer-style UI (ID - Name format)
                              <select
                                className={`form-control border-0 ${buttonClass}`}
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                }}
                                value={cell.karigarId || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    rowIndex,
                                    colIndex,
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">Select Karigar</option>
                                {karigars
                                  .filter(
                                    (k) => k.status === "1" || k.status === 1
                                  )
                                  .map((k) => (
                                    <option key={k.id} value={k.id}>
                                      {k.id} -{" "}
                                      {k.companyName
                                        ? `${k.fullName} (${k.companyName})`
                                        : k.fullName || k.name}
                                    </option>
                                  ))}
                              </select>
                            ) : isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    // Show dropdown when typing, but for productGroup only after 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    // Check if this is a column that requires 3+ characters
                                    const requiresMinLength =
                                      columnType === "productGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      // Use setTimeout to ensure DOM is updated before calculating position
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    // For productGroup, only show dropdown if 3+ characters
                                    if (columnType === "productGroup") {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }
                                    } else {
                                      // For other columns, show dropdown normally
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType
                                      );
                                    }
                                  }}
                                  className={`form-control border-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                  (fileInputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />
                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";
                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(cell.imagePreview);
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `tooltip-${rowIndex}-${colIndex}`
                                      ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image"
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(cell.imagePreview);
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "otherAmount" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className={`form-control border-0 rounded-0 ${buttonClass}`}
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const columnType = COLUMNS[colIndex].id;

                                  // Special handling for text fields - allow any text
                                  if (
                                    columnType === "design" ||
                                    columnType === "productType" ||
                                    columnType === "issueDescription" ||
                                    columnType === "lType"
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                  // For numeric fields only allow numbers and decimal
                                  else if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className={`form-control border-0 rounded-0 ${buttonClass}`}
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      position: tableData.length > 6 ? "sticky" : undefined,
                      bottom: tableData.length > 6 ? 0 : undefined,
                      zIndex: tableData.length > 6 ? 5 : undefined,
                    }}
                  >
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Current Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const currentWt = parseFloat(
                                row.data.find(
                                  (cell) => cell.id === "currentWeight"
                                )?.value || 0
                              );
                              return sum + (isNaN(currentWt) ? 0 : currentWt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Exp Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const expWt = parseFloat(
                                row.data.find((cell) => cell.id === "expWeight")
                                  ?.value || 0
                              );
                              return sum + (isNaN(expWt) ? 0 : expWt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Issue Description */}
                    <td className="p-0"></td> {/* Lbr Type */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr % */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrPercent = parseFloat(
                                row.data.find(
                                  (cell) => cell.id === "lbrPercentage"
                                )?.value || 0
                              );
                              return sum + (isNaN(lbrPercent) ? 0 : lbrPercent);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Rate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrRate = parseFloat(
                                row.data.find((cell) => cell.id === "lbrRate")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lbrRate) ? 0 : lbrRate);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Amount */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmount = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmount")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lbrAmount) ? 0 : lbrAmount);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Image */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Other Amount */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find(
                                  (cell) => cell.id === "otherAmount"
                                )?.value || 0
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Total Amount */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmount = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmount")
                                  ?.value || 0
                              );
                              const othAmt = parseFloat(
                                row.data.find(
                                  (cell) => cell.id === "otherAmount"
                                )?.value || 0
                              );
                              return (
                                sum +
                                (isNaN(lbrAmount) ? 0 : lbrAmount) +
                                (isNaN(othAmt) ? 0 : othAmt)
                              );
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-end mt-3">
              {/* <button className="btn btn-primary" onClick={handleSubmit}>
                Add
              </button> */}
            </div>

            <PaymentSection
              paymentRows={paymentRows}
              onPaymentRowsChange={setPaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              finalAmount={calculateTotal().toFixed(2)}
              finalAmountLabel="Approx Final Amount:"
              grandTotalLabel="Approx Total"
              showDiscount={false}
              showGst={false}
              excludedPayModes={["old_gold", "old_silver", "kasar", "scheme"]}
              showCashPaid={false}
              roundOffAmount={getDisplayRoundOff()}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={parseFloat(calculatePaymentTotal()) || 0}
              payableAmount={calculatePayableAmount()}
              remainingAmount={calculateRemainingAmount()}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  {!isViewOnly &&
                    ((isEditing && canEditRepairingOrder) ||
                      (!isEditing && canAddRepairingOrder)) && (
                      <button
                        className={`btn btn-primary ${buttonClass}`}
                        onClick={handleSubmit}
                      >
                        Save
                      </button>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderImageModal()}
      {renderOtherAmountModal()}
      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default AddRepairingOrder;
